<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import {
  addDays,
  addMonths,
  addWeeks,
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns';

import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import FeatureLockedScreen from 'dashboard/components/FeatureLockedScreen.vue';
import agendaAPI from 'dashboard/api/agenda';
import agentsAPI from 'dashboard/api/agents';
import contactsAPI from 'dashboard/api/contacts';
import inboxesAPI from 'dashboard/api/inboxes';

import AgendaCalendar from './components/AgendaCalendar.vue';
import AgendaBookingModal from './components/AgendaBookingModal.vue';
import AgendaScheduleModal from './components/AgendaScheduleModal.vue';

// ─── Feature gate ────────────────────────────────────────
const { accountId } = useAccount();
const isFeatureEnabled = useMapGetter('accounts/isFeatureEnabledonAccount');
const hasAgenda = computed(() =>
  isFeatureEnabled.value(accountId.value, 'agenda')
);

// ─── Constants ───────────────────────────────────────────
const TABS = [
  { key: 'calendar', label: 'Calendário', icon: 'i-lucide-calendar-days' },
  { key: 'bookings', label: 'Agendamentos', icon: 'i-lucide-list-checks' },
  { key: 'resources', label: 'Profissionais', icon: 'i-lucide-users' },
  { key: 'kpi', label: 'Relatórios', icon: 'i-lucide-bar-chart-3' },
  { key: 'settings', label: 'Configurações', icon: 'i-lucide-settings-2' },
];

const STATUS_CONFIG = {
  confirmed: {
    label: 'Confirmado',
    classes: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
  pending: {
    label: 'Pendente',
    classes: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  completed: {
    label: 'Concluído',
    classes: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  cancelled: {
    label: 'Cancelado',
    classes: 'bg-red-50 text-red-600 border-red-200',
  },
  no_show: {
    label: 'No-show',
    classes: 'bg-slate-50 text-slate-500 border-slate-200',
  },
};

// Inline hex colors for status — avoids Tailwind purge of dynamic classes
const STATUS_COLOR = {
  confirmed: '#10b981',
  pending: '#f59e0b',
  completed: '#3b82f6',
  cancelled: '#ef4444',
  no_show: '#94a3b8',
};

const MONTHS_PT = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

const STAT_PERIODS = [
  { key: 'today', label: 'Hoje' },
  { key: 'week', label: 'Esta semana' },
  { key: 'month', label: 'Este mês' },
  { key: '30p', label: 'Últ. 30 dias' },
  { key: '3m', label: 'Últ. 3 meses' },
  { key: 'year', label: 'Este ano' },
  { key: 'all', label: 'Tudo' },
  { key: 'custom', label: 'Personalizado' },
];

// ─── Alert ───────────────────────────────────────────────

// ─── State ───────────────────────────────────────────────
const isLoading = ref(false);
const isSavingSchedule = ref(false);
const isSavingBooking = ref(false);
const isSavingResource = ref(false);

const schedules = ref([]);
const resources = ref([]);
const bookings = ref([]);
const availability = ref([]);
const agents = ref([]);
const stats = ref(null);
const isLoadingStats = ref(false);
const statsPeriod = ref('month');
const statsBookings = ref([]);
const customStartDate = ref(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
const customEndDate = ref(format(new Date(), 'yyyy-MM-dd'));

const selectedScheduleId = ref(null);
const activeTab = ref('calendar');
const calendarView = ref('month');
const anchorDate = ref(new Date());
const selectedDate = ref(new Date());

// Modals
const showBookingModal = ref(false);
const showBlockModal = ref(false);
const isEditingBlock = ref(false);
const showScheduleModal = ref(false);
const editingSchedule = ref(null);
const editingBooking = ref(null); // booking being edited
const preselectedSlot = ref(null);

// Slot detail modal
const showSlotDetail = ref(false);
const slotDetailItem = ref(null); // { slot, bookings }

// Webhook secret
const isRegeneratingSecret = ref(false);

// Message modal
const showMessageModal = ref(false);
const messageModalBooking = ref(null);
const messageDraft = ref('');
const selectedInboxId = ref(null);
const isSendingMessage = ref(false);
const inboxes = ref([]);

// Quick contact modal
const showQuickContactModal = ref(false);
const quickContactName = ref('');
const quickContactPhoneDisplay = ref('');
const isSavingQuickContact = ref(false);

// Resources panel
const showResourceForm = ref(false);
const resourceForm = ref({
  name: '',
  email: '',
  phone: '',
  color: '#6366f1',
  source_type: 'custom',
});
const editingResourceId = ref(null);
const showAgentImport = ref(false);

// Bookings filter / list
const bookingFilter = ref('all');
const bookingSearch = ref('');
const bookingProductFilter = ref('');
const bookingResourceFilter = ref('');
const bookingTypeFilter = ref('');
const showProductFilter = ref(false);
const showResourceFilter = ref(false);
const showTypeFilter = ref(false);
const bookingPeriod = ref('today');
const listCustomStart = ref(format(new Date(), 'yyyy-MM-dd'));
const listCustomEnd = ref(format(addDays(new Date(), 7), 'yyyy-MM-dd'));
const listBookings = ref([]);
const isLoadingList = ref(false);
const now = ref(new Date());

// Settings form (for active schedule)
const settingsForm = ref({
  integration_settings: {
    availability_webhook_url: '',
    booking_webhook_url: '',
    google_meet_enabled: false,
    calendar_id: '',
    ical_secret_url: '',
    products: [],
    appointment_types: [],
    booking_origins: ['WhatsApp', 'Instagram', 'Balcão'],
  },
});
const newSettingsProduct = ref('');
const newSettingsType = ref('');
const newSettingsOrigin = ref('');

const isConnectingGoogle = ref(false);
const isTestingIcal = ref(false);
const icalTestResult = ref(null);

// ─── Computed ────────────────────────────────────────────
const selectedSchedule = computed(
  () => schedules.value.find(s => s.id === selectedScheduleId.value) || null
);

const importedUserIds = computed(
  () => new Set(resources.value.map(r => r.user_id).filter(Boolean))
);

const availableAgents = computed(() =>
  agents.value.filter(a => !importedUserIds.value.has(a.id))
);

const scheduleResources = computed(() => {
  if (!selectedSchedule.value) return resources.value;
  // Schedule already embeds its own resources; fall back to global list
  const embedded = selectedSchedule.value.resources;
  if (embedded?.length > 0) return embedded;
  const ids = new Set(selectedSchedule.value.resource_ids || []);
  return ids.size > 0
    ? resources.value.filter(r => ids.has(r.id))
    : resources.value;
});

const scheduleOriginOptions = computed(() => {
  const origins = selectedSchedule.value?.integration_settings?.booking_origins;
  return origins?.length > 0 ? origins : ['WhatsApp', 'Instagram', 'Balcão'];
});

const calendarTitle = computed(() => {
  if (calendarView.value === 'month') {
    return `${MONTHS_PT[anchorDate.value.getMonth()]} ${anchorDate.value.getFullYear()}`;
  }
  if (calendarView.value === 'week') {
    const start = startOfWeek(anchorDate.value, { weekStartsOn: 0 });
    const end = endOfWeek(anchorDate.value, { weekStartsOn: 0 });
    return `${format(start, 'dd')}–${format(end, 'dd')} ${MONTHS_PT[start.getMonth()]} ${start.getFullYear()}`;
  }
  return `${format(anchorDate.value, 'dd')} de ${MONTHS_PT[anchorDate.value.getMonth()]} ${anchorDate.value.getFullYear()}`;
});

const dayBookings = computed(() =>
  bookings.value.filter(b => {
    try {
      return (
        format(parseISO(b.starts_at), 'yyyy-MM-dd') ===
        format(selectedDate.value, 'yyyy-MM-dd')
      );
    } catch {
      return false;
    }
  })
);

const daySlots = computed(() =>
  availability.value.filter(s => {
    try {
      return s.starts_at?.startsWith(format(selectedDate.value, 'yyyy-MM-dd'));
    } catch {
      return false;
    }
  })
);

const filteredBookings = computed(() => {
  let result = bookings.value;
  if (bookingFilter.value !== 'all') {
    result = result.filter(b => b.status === bookingFilter.value);
  }
  if (bookingSearch.value.trim()) {
    const q = bookingSearch.value.toLowerCase();
    result = result.filter(
      b =>
        b.attendee_name?.toLowerCase().includes(q) ||
        b.attendee_email?.toLowerCase().includes(q) ||
        b.resource?.name?.toLowerCase().includes(q)
    );
  }
  return result;
});

const listDateRange = computed(() => {
  const today = new Date();
  switch (bookingPeriod.value) {
    case 'today':
      return { start: startOfDay(today), end: endOfDay(today) };
    case 'week':
      return {
        start: startOfWeek(today, { weekStartsOn: 0 }),
        end: endOfWeek(today, { weekStartsOn: 0 }),
      };
    case 'month':
      return { start: startOfMonth(today), end: endOfMonth(today) };
    case 'custom':
      return {
        start: listCustomStart.value
          ? startOfDay(parseISO(listCustomStart.value))
          : startOfDay(today),
        end: listCustomEnd.value
          ? endOfDay(parseISO(listCustomEnd.value))
          : endOfDay(today),
      };
    default:
      return { start: startOfDay(today), end: endOfDay(today) };
  }
});

// Normalize product field: can be string (legacy) or array
const bookingProducts = b => {
  const p = b.metadata?.product;
  if (!p) return [];
  return Array.isArray(p) ? p : [p];
};

const uniqueListProducts = computed(() => {
  const set = new Set();
  listBookings.value.forEach(b => bookingProducts(b).forEach(p => set.add(p)));
  return [...set].sort();
});

const uniqueListTypes = computed(() => {
  const set = new Set();
  listBookings.value.forEach(b => {
    if (b.metadata?.appointment_type) set.add(b.metadata.appointment_type);
  });
  return [...set].sort();
});

const uniqueListResources = computed(() => {
  const map = new Map();
  listBookings.value.forEach(b => {
    if (b.resource) map.set(b.resource.id, b.resource);
  });
  return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
});

const filteredListBookings = computed(() => {
  let result = listBookings.value;
  if (bookingFilter.value !== 'all') {
    result = result.filter(b => b.status === bookingFilter.value);
  }
  if (bookingSearch.value.trim()) {
    const q = bookingSearch.value.toLowerCase();
    result = result.filter(
      b =>
        b.attendee_name?.toLowerCase().includes(q) ||
        b.attendee_email?.toLowerCase().includes(q) ||
        bookingProducts(b).some(p => p.toLowerCase().includes(q)) ||
        b.metadata?.appointment_type?.toLowerCase().includes(q) ||
        b.resource?.name?.toLowerCase().includes(q)
    );
  }
  if (bookingTypeFilter.value) {
    result = result.filter(
      b => b.metadata?.appointment_type === bookingTypeFilter.value
    );
  }
  if (bookingProductFilter.value) {
    result = result.filter(b =>
      bookingProducts(b).includes(bookingProductFilter.value)
    );
  }
  if (bookingResourceFilter.value) {
    result = result.filter(
      b => String(b.resource?.id) === bookingResourceFilter.value
    );
  }
  return [...result].sort(
    (a, b) => new Date(a.starts_at) - new Date(b.starts_at)
  );
});

const nextListBookingId = computed(() => {
  const upcoming = filteredListBookings.value.filter(
    b => !isBookingNow(b) && !isBookingPast(b)
  );
  return upcoming[0]?.id ?? null;
});

const nextDayBookingId = computed(() => {
  const upcoming = [...dayBookings.value]
    .filter(b => !isBookingNow(b) && parseISO(b.starts_at) > now.value)
    .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
  return upcoming[0]?.id ?? null;
});

const isBookingPast = b => {
  try {
    return parseISO(b.ends_at) < now.value;
  } catch {
    return false;
  }
};

const isBookingNow = b => {
  try {
    return (
      parseISO(b.starts_at) <= now.value && now.value <= parseISO(b.ends_at)
    );
  } catch {
    return false;
  }
};

const googleConnectedEmail = computed(
  () =>
    selectedSchedule.value?.integration_settings?.google_connected_email || null
);

const incomingWebhookUrl = computed(() => {
  if (!selectedSchedule.value) return '';
  const accountId = window.location.pathname.split('/')[3];
  const secret = selectedSchedule.value.integration_settings?.webhook_secret;
  if (!secret) return '';
  return `${window.location.origin}/webhooks/agenda/${accountId}/${secret}`;
});

const copyToClipboard = text => {
  window.navigator.clipboard.writeText(text);
  useAlert('Copiado!');
};

const regenerateWebhookSecret = async () => {
  if (!selectedSchedule.value) return;
  if (
    !confirm(
      'Isso invalidará a URL atual. O N8N precisará ser atualizado com a nova URL. Continuar?'
    )
  )
    return;
  isRegeneratingSecret.value = true;
  try {
    const res = await agendaAPI.regenerateWebhookSecret(
      selectedSchedule.value.id
    );
    const idx = schedules.value.findIndex(
      s => s.id === selectedSchedule.value.id
    );
    if (idx >= 0) {
      schedules.value[idx] = {
        ...schedules.value[idx],
        integration_settings: {
          ...schedules.value[idx].integration_settings,
          webhook_secret: res.data.webhook_secret,
        },
      };
    }
    useAlert('Secret regenerado com sucesso.');
  } catch {
    useAlert('Não foi possível regenerar o secret.');
  } finally {
    isRegeneratingSecret.value = false;
  }
};

const contactUrl = contactId => {
  const accountId = window.location.pathname.split('/')[3];
  return `${window.location.origin}/app/accounts/${accountId}/contacts/${contactId}`;
};

const statsDateRange = computed(() => {
  const now = new Date();
  switch (statsPeriod.value) {
    case 'today':
      return { start: startOfDay(now), end: endOfDay(now) };
    case 'week':
      return {
        start: startOfWeek(now, { weekStartsOn: 1 }),
        end: endOfWeek(now, { weekStartsOn: 1 }),
      };
    case 'month':
      return { start: startOfMonth(now), end: endOfMonth(now) };
    case '30p':
      return { start: subDays(now, 30), end: now };
    case '3m':
      return { start: subMonths(now, 3), end: now };
    case 'year':
      return { start: startOfYear(now), end: endOfYear(now) };
    case 'all':
      return { start: null, end: null };
    case 'custom': {
      const s = customStartDate.value
        ? new Date(customStartDate.value + 'T00:00:00')
        : null;
      const e = customEndDate.value
        ? new Date(customEndDate.value + 'T23:59:59')
        : null;
      return { start: s, end: e };
    }
    default:
      return { start: startOfMonth(now), end: endOfMonth(now) };
  }
});

const statsPeriodLabel = computed(() => {
  if (statsPeriod.value === 'all') return 'todo o período';
  const { start, end } = statsDateRange.value;
  if (!start && !end) return 'todo o período';
  const fmt = d => (d ? format(d, 'dd/MM/yy') : '?');
  return `${fmt(start)} – ${fmt(end)}`;
});

// ─── Data loading ─────────────────────────────────────────
const loadSchedules = async () => {
  try {
    const res = await agendaAPI.get();
    schedules.value = res.data?.payload || res.data || [];
    if (!selectedScheduleId.value && schedules.value.length > 0) {
      selectedScheduleId.value = schedules.value[0].id;
    }
  } catch {
    useAlert('Não foi possível carregar as agendas.');
  }
};

const loadResources = async () => {
  try {
    const res = await agendaAPI.getResources();
    resources.value = res.data?.payload || res.data || [];
  } catch {
    // silent
  }
};

const loadBookings = async () => {
  if (!selectedScheduleId.value) return;
  try {
    const start = startOfMonth(anchorDate.value);
    const end = endOfMonth(anchorDate.value);
    const res = await agendaAPI.getBookings({
      appointment_schedule_id: selectedScheduleId.value,
      start_at: start.toISOString(),
      end_at: end.toISOString(),
    });
    bookings.value = res.data?.payload || res.data || [];
  } catch {
    // silent
  }
};

const loadListBookings = async () => {
  if (!selectedScheduleId.value) return;
  isLoadingList.value = true;
  try {
    const { start, end } = listDateRange.value;
    const res = await agendaAPI.getBookings({
      appointment_schedule_id: selectedScheduleId.value,
      start_at: start.toISOString(),
      end_at: end.toISOString(),
    });
    listBookings.value = res.data?.payload || res.data || [];
  } catch {
    // silent
  } finally {
    isLoadingList.value = false;
  }
};

const loadAvailability = async () => {
  if (!selectedScheduleId.value) return;
  try {
    const start = startOfMonth(anchorDate.value);
    const end = endOfMonth(anchorDate.value);
    const res = await agendaAPI.getAvailability(selectedScheduleId.value, {
      start_at: start.toISOString(),
      end_at: end.toISOString(),
    });
    availability.value = res.data?.slots || [];
  } catch {
    // silent
  }
};

const loadStats = async () => {
  if (!selectedScheduleId.value) return;
  isLoadingStats.value = true;
  try {
    const { start, end } = statsDateRange.value;
    const res = await agendaAPI.getBookingStats({
      schedule_id: selectedScheduleId.value,
      start_at: start?.toISOString(),
      end_at: end?.toISOString(),
    });
    stats.value = res.data || {};
  } catch {
    stats.value = {};
  } finally {
    isLoadingStats.value = false;
  }
};

const loadStatsBookings = async () => {
  if (!selectedScheduleId.value) return;
  try {
    const { start, end } = statsDateRange.value;
    const res = await agendaAPI.getBookings({
      appointment_schedule_id: selectedScheduleId.value,
      start_at: start?.toISOString(),
      end_at: end?.toISOString(),
    });
    statsBookings.value = res.data?.payload || res.data || [];
  } catch {
    statsBookings.value = [];
  }
};

const reloadKpi = () => {
  stats.value = null;
  statsBookings.value = [];
  return Promise.all([loadStats(), loadStatsBookings()]);
};

const loadAgents = async () => {
  if (agents.value.length > 0) return;
  try {
    const res = await agentsAPI.get();
    agents.value = res.data || [];
  } catch {
    // silent
  }
};

const loadAll = async () => {
  isLoading.value = true;
  try {
    await loadSchedules();
    await Promise.all([loadResources(), loadBookings(), loadAvailability()]);
  } finally {
    isLoading.value = false;
  }
};

// ─── Watch schedule change ────────────────────────────────
watch(selectedScheduleId, async () => {
  if (!selectedScheduleId.value) return;
  availability.value = [];
  bookings.value = [];
  stats.value = null;
  const tasks = [loadBookings(), loadAvailability()];
  if (activeTab.value === 'kpi') tasks.push(reloadKpi());
  await Promise.all(tasks);

  // Sync settings form
  const s = selectedSchedule.value;
  if (s?.integration_settings) {
    const si = s.integration_settings;
    settingsForm.value.integration_settings = {
      availability_webhook_url: si.availability_webhook_url || '',
      booking_webhook_url: si.booking_webhook_url || '',
      google_meet_enabled: si.google_meet_enabled === true,
      calendar_id: si.calendar_id || '',
      ical_secret_url: si.ical_secret_url || '',
      products: [...(si.products || [])],
      appointment_types: [...(si.appointment_types || [])],
      booking_origins:
        si.booking_origins?.length > 0
          ? [...si.booking_origins]
          : ['WhatsApp', 'Instagram', 'Balcão'],
    };
  }
});

watch(activeTab, tab => {
  if (tab === 'kpi') reloadKpi();
  if (tab === 'resources') loadAgents();
  if (tab === 'bookings') loadListBookings();
});

watch(bookingPeriod, () => {
  if (activeTab.value === 'bookings') loadListBookings();
});

watch([listCustomStart, listCustomEnd], () => {
  if (activeTab.value === 'bookings' && bookingPeriod.value === 'custom')
    loadListBookings();
});

watch(statsPeriod, reloadKpi);
watch([customStartDate, customEndDate], () => {
  if (statsPeriod.value === 'custom') reloadKpi();
});

watch(anchorDate, () => {
  loadBookings();
  loadAvailability();
});

// ─── Calendar navigation ──────────────────────────────────
const navigate = dir => {
  if (calendarView.value === 'month') {
    anchorDate.value =
      dir > 0 ? addMonths(anchorDate.value, 1) : subMonths(anchorDate.value, 1);
  } else if (calendarView.value === 'week') {
    anchorDate.value =
      dir > 0 ? addWeeks(anchorDate.value, 1) : subWeeks(anchorDate.value, 1);
  } else {
    anchorDate.value = addDays(anchorDate.value, dir);
  }
};

const goToday = () => {
  anchorDate.value = new Date();
  selectedDate.value = new Date();
};

// ─── Schedule CRUD ────────────────────────────────────────
const openNewSchedule = () => {
  editingSchedule.value = null;
  showScheduleModal.value = true;
};

const openEditSchedule = () => {
  editingSchedule.value = selectedSchedule.value;
  showScheduleModal.value = true;
};

const saveSchedule = async data => {
  isSavingSchedule.value = true;
  try {
    if (editingSchedule.value) {
      const res = await agendaAPI.update(editingSchedule.value.id, data);
      const updated = res.data?.payload || res.data;
      const idx = schedules.value.findIndex(s => s.id === updated.id);
      if (idx >= 0) schedules.value[idx] = updated;
    } else {
      const res = await agendaAPI.create(data);
      const created = res.data?.payload || res.data;
      schedules.value.unshift(created);
      selectedScheduleId.value = created.id;
    }
    showScheduleModal.value = false;
    useAlert('Agenda salva com sucesso.');
  } catch {
    useAlert('Não foi possível salvar a agenda.');
  } finally {
    isSavingSchedule.value = false;
  }
};

const deleteSchedule = async () => {
  if (!selectedSchedule.value) return;
  if (
    !confirm(
      'Tem certeza que deseja excluir esta agenda? Todos os agendamentos serão perdidos.'
    )
  )
    return;
  try {
    await agendaAPI.delete(selectedSchedule.value.id);
    schedules.value = schedules.value.filter(
      s => s.id !== selectedSchedule.value.id
    );
    selectedScheduleId.value = schedules.value[0]?.id || null;
    useAlert('Agenda excluída.');
  } catch {
    useAlert('Não foi possível excluir a agenda.');
  }
};

// ─── Quick Contact ─────────────────────────────────────────
const formatPhoneMask = value => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length === 0) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10)
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

const onQuickPhoneInput = e => {
  quickContactPhoneDisplay.value = formatPhoneMask(e.target.value);
};

const quickContactE164 = computed(() => {
  const digits = quickContactPhoneDisplay.value.replace(/\D/g, '');
  return digits.length >= 10 ? `+55${digits}` : '';
});

const openQuickContactModal = () => {
  quickContactName.value = '';
  quickContactPhoneDisplay.value = '';
  showQuickContactModal.value = true;
};

const saveQuickContact = async () => {
  if (!quickContactName.value.trim()) {
    useAlert('Informe o nome do contato.');
    return;
  }
  if (!quickContactE164.value) {
    useAlert('Informe um telefone válido com DDD (10 ou 11 dígitos).');
    return;
  }
  isSavingQuickContact.value = true;
  try {
    await contactsAPI.create({
      name: quickContactName.value.trim(),
      phone_number: quickContactE164.value,
    });
    showQuickContactModal.value = false;
    useAlert(`Contato "${quickContactName.value.trim()}" criado com sucesso.`);
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      'Não foi possível criar o contato.';
    useAlert(msg);
  } finally {
    isSavingQuickContact.value = false;
  }
};

// ─── Booking CRUD ─────────────────────────────────────────
const openNewBooking = (slot = null) => {
  if (!selectedScheduleId.value) {
    useAlert('Selecione uma agenda primeiro.');
    return;
  }
  preselectedSlot.value = slot;
  showBookingModal.value = true;
};

const openBlockModal = () => {
  if (!selectedScheduleId.value) {
    useAlert('Selecione uma agenda primeiro.');
    return;
  }
  editingBooking.value = null;
  isEditingBlock.value = false;
  showBlockModal.value = true;
};

const saveBooking = async data => {
  isSavingBooking.value = true;
  try {
    const res = await agendaAPI.createBooking(data);
    const created = res.data?.payload || res.data;
    bookings.value.push(created);
    showBookingModal.value = false;
    await loadAvailability();
    if (activeTab.value === 'bookings') loadListBookings();
    useAlert('Agendamento criado com sucesso.');
  } catch {
    useAlert('Não foi possível criar o agendamento.');
  } finally {
    isSavingBooking.value = false;
  }
};

const saveBlock = async data => {
  isSavingBooking.value = true;
  try {
    const res = await agendaAPI.createBooking(data);
    const created = res.data?.payload || res.data;
    bookings.value.push(created);
    showBlockModal.value = false;
    await loadAvailability();
    useAlert('Horário bloqueado com sucesso.');
  } catch {
    useAlert('Não foi possível bloquear o horário.');
  } finally {
    isSavingBooking.value = false;
  }
};

const updateBlock = async data => {
  isSavingBooking.value = true;
  try {
    const res = await agendaAPI.updateBooking(data.id, data);
    const updated = res.data?.payload || res.data;
    const idx = bookings.value.findIndex(b => b.id === updated.id);
    if (idx >= 0) bookings.value[idx] = updated;
    showBlockModal.value = false;
    editingBooking.value = null;
    await loadAvailability();
    useAlert('Bloqueio atualizado.');
  } catch {
    useAlert('Não foi possível atualizar o bloqueio.');
  } finally {
    isSavingBooking.value = false;
  }
};

const updateBookingStatus = async (booking, status) => {
  try {
    const res = await agendaAPI.updateBooking(booking.id, { status });
    const updated = res.data?.payload || res.data;
    const idx = bookings.value.findIndex(b => b.id === booking.id);
    if (idx >= 0) bookings.value[idx] = updated;
    const listIdx = listBookings.value.findIndex(b => b.id === booking.id);
    if (listIdx >= 0) listBookings.value[listIdx] = updated;
    useAlert('Status atualizado.');
  } catch {
    useAlert('Não foi possível atualizar o agendamento.');
  }
};

const moveBooking = async ({ id, starts_at, ends_at }) => {
  const idx = bookings.value.findIndex(b => b.id === id);
  if (idx < 0) return;
  const original = { ...bookings.value[idx] };
  bookings.value[idx] = { ...bookings.value[idx], starts_at, ends_at };
  try {
    const res = await agendaAPI.updateBooking(id, { starts_at, ends_at });
    const updated = res.data?.payload || res.data;
    bookings.value[idx] = updated;
    await loadAvailability();
  } catch {
    bookings.value[idx] = original;
    useAlert('Não foi possível mover o agendamento.');
  }
};

const openEditBooking = booking => {
  editingBooking.value = booking;
  preselectedSlot.value = null;
  if (booking.booking_type === 'block') {
    isEditingBlock.value = true;
    showBlockModal.value = true;
  } else {
    showBookingModal.value = true;
  }
};

const updateBooking = async data => {
  isSavingBooking.value = true;
  try {
    const res = await agendaAPI.updateBooking(data.id, data);
    const updated = res.data?.payload || res.data;
    const idx = bookings.value.findIndex(b => b.id === updated.id);
    if (idx >= 0) bookings.value[idx] = updated;
    showBookingModal.value = false;
    editingBooking.value = null;
    await Promise.all([loadBookings(), loadAvailability()]);
    if (activeTab.value === 'bookings') {
      // If rescheduled outside the current list range, widen to current month so booking stays visible
      const newStartsAt = new Date(updated.starts_at);
      const { start, end } = listDateRange.value;
      if (newStartsAt < start || newStartsAt > end) {
        bookingPeriod.value = 'month'; // watch(bookingPeriod) triggers loadListBookings()
      } else {
        loadListBookings();
      }
    }
    useAlert('Agendamento atualizado.');
  } catch (e) {
    const msg =
      e?.response?.data?.message ||
      e?.response?.data?.errors?.join(', ') ||
      e?.message ||
      '';
    useAlert(
      msg ? `Erro: ${msg}` : 'Não foi possível atualizar o agendamento.'
    );
  } finally {
    isSavingBooking.value = false;
  }
};

// Slot detail modal
const openSlotDetail = slot => {
  const slotBookings = bookings.value.filter(b => {
    try {
      return (
        b.starts_at === slot.starts_at ||
        (parseISO(b.starts_at) >= parseISO(slot.starts_at) &&
          parseISO(b.starts_at) < parseISO(slot.ends_at))
      );
    } catch {
      return false;
    }
  });
  slotDetailItem.value = { slot, bookings: slotBookings };
  showSlotDetail.value = true;
};

// ─── Message sending ──────────────────────────────────────
const buildMessageDraft = booking => {
  const name = booking.attendee_name || 'cliente';
  const date = format(parseISO(booking.starts_at), 'dd/MM/yyyy');
  const start = format(parseISO(booking.starts_at), 'HH:mm');
  const end = format(parseISO(booking.ends_at), 'HH:mm');
  const resource = booking.resource?.name
    ? `\n👤 Profissional: ${booking.resource.name}`
    : '';
  const schedule = selectedSchedule.value?.title
    ? `\n📍 ${selectedSchedule.value.title}`
    : '';
  return `Olá, ${name}! ✅ Seu agendamento está confirmado:\n\n📅 Data: ${date}\n⏰ Horário: ${start} – ${end}${resource}${schedule}\n\nEm caso de dúvidas, estamos à disposição!`;
};

const loadInboxes = async () => {
  if (inboxes.value.length > 0) return;
  try {
    const res = await inboxesAPI.get();
    inboxes.value = res.data?.payload || res.data || [];
  } catch {
    // silent
  }
};

const openMessageModal = async booking => {
  messageModalBooking.value = booking;
  messageDraft.value = buildMessageDraft(booking);
  showMessageModal.value = true;
  await loadInboxes();
  if (!selectedInboxId.value && inboxes.value.length > 0) {
    selectedInboxId.value = inboxes.value[0].id;
  }
};

const sendMessage = async () => {
  if (!selectedInboxId.value || !messageDraft.value.trim()) return;
  const contactId = messageModalBooking.value?.metadata?.contact_id;
  if (!contactId) return;
  isSendingMessage.value = true;
  try {
    await agendaAPI.sendBookingMessage(
      contactId,
      selectedInboxId.value,
      messageDraft.value
    );
    useAlert('Mensagem enviada com sucesso!');
    showMessageModal.value = false;
  } catch {
    useAlert('Não foi possível enviar a mensagem.');
  } finally {
    isSendingMessage.value = false;
  }
};

// ─── Resource CRUD ────────────────────────────────────────
const openResourceForm = (resource = null) => {
  if (resource) {
    editingResourceId.value = resource.id;
    resourceForm.value = {
      name: resource.name,
      email: resource.email || '',
      phone: resource.phone || '',
      color: resource.color || '#6366f1',
      source_type: resource.source_type,
    };
  } else {
    editingResourceId.value = null;
    resourceForm.value = {
      name: '',
      email: '',
      phone: '',
      color: '#6366f1',
      source_type: 'custom',
    };
  }
  showResourceForm.value = true;
};

const saveResource = async () => {
  if (!resourceForm.value.name.trim()) return;
  isSavingResource.value = true;
  try {
    if (editingResourceId.value) {
      const res = await agendaAPI.updateResource(
        editingResourceId.value,
        resourceForm.value
      );
      const updated = res.data?.payload || res.data;
      const idx = resources.value.findIndex(r => r.id === updated.id);
      if (idx >= 0) resources.value[idx] = updated;
    } else {
      const res = await agendaAPI.createResource({
        ...resourceForm.value,
      });
      const created = res.data?.payload || res.data;
      resources.value.push(created);
    }
    showResourceForm.value = false;
    useAlert('Profissional salvo.');
  } catch {
    useAlert('Não foi possível salvar o profissional.');
  } finally {
    isSavingResource.value = false;
  }
};

const importAgent = async agent => {
  isSavingResource.value = true;
  try {
    const res = await agendaAPI.createResource({
      source_type: 'platform_user',
      user_id: agent.id,
    });
    resources.value.push(res.data?.payload || res.data);
    useAlert('Agente importado com sucesso.');
    if (availableAgents.value.length === 0) showAgentImport.value = false;
  } catch (err) {
    useAlert(
      err?.response?.data?.message || 'Não foi possível importar o agente.'
    );
  } finally {
    isSavingResource.value = false;
  }
};

const deleteResource = async resource => {
  if (!confirm(`Remover ${resource.name}?`)) return;
  try {
    await agendaAPI.deleteResource(resource.id);
    resources.value = resources.value.filter(r => r.id !== resource.id);
    useAlert('Profissional removido.');
  } catch {
    useAlert('Não foi possível remover o profissional.');
  }
};

// ─── Products (settings tab) ──────────────────────────────
const addSettingsProduct = () => {
  const name = newSettingsProduct.value.trim();
  if (!name || settingsForm.value.integration_settings.products.includes(name))
    return;
  settingsForm.value.integration_settings.products.push(name);
  newSettingsProduct.value = '';
};

const removeSettingsProduct = i =>
  settingsForm.value.integration_settings.products.splice(i, 1);

// ─── Appointment types (settings tab) ────────────────────
const addSettingsType = () => {
  const name = newSettingsType.value.trim();
  if (
    !name ||
    settingsForm.value.integration_settings.appointment_types.includes(name)
  )
    return;
  settingsForm.value.integration_settings.appointment_types.push(name);
  newSettingsType.value = '';
};

const removeSettingsType = i =>
  settingsForm.value.integration_settings.appointment_types.splice(i, 1);

// ─── Booking origins (settings tab) ──────────────────────
const addSettingsOrigin = () => {
  const name = newSettingsOrigin.value.trim();
  if (
    !name ||
    settingsForm.value.integration_settings.booking_origins.includes(name)
  )
    return;
  settingsForm.value.integration_settings.booking_origins.push(name);
  newSettingsOrigin.value = '';
};

const removeSettingsOrigin = i =>
  settingsForm.value.integration_settings.booking_origins.splice(i, 1);

// ─── Settings save ────────────────────────────────────────
const saveSettings = async () => {
  if (!selectedSchedule.value) return;
  isSavingSchedule.value = true;
  try {
    const si = {
      ...(selectedSchedule.value.integration_settings || {}),
      ...settingsForm.value.integration_settings,
    };
    const res = await agendaAPI.update(selectedSchedule.value.id, {
      integration_settings: si,
    });
    const updated = res.data?.payload || res.data;
    const idx = schedules.value.findIndex(s => s.id === updated.id);
    if (idx >= 0) schedules.value[idx] = updated;
    useAlert('Configurações salvas.');
  } catch {
    useAlert('Não foi possível salvar as configurações.');
  } finally {
    isSavingSchedule.value = false;
  }
};

const connectGoogleCalendar = async () => {
  if (!selectedSchedule.value) return;
  isConnectingGoogle.value = true;
  try {
    const res = await agendaAPI.getGoogleCalendarAuthUrl(
      selectedSchedule.value.id
    );
    const url = res.data?.url;
    if (url) window.location.href = url;
    else useAlert('Não foi possível gerar o link de autorização.');
  } catch {
    useAlert(
      'Erro ao conectar com o Google. Verifique se as credenciais OAuth estão configuradas no servidor.'
    );
  } finally {
    isConnectingGoogle.value = false;
  }
};

const testIcalConnection = async () => {
  if (!selectedSchedule.value) return;
  isTestingIcal.value = true;
  icalTestResult.value = null;
  try {
    // Save first so the URL is persisted before testing
    await saveSettings();
    const res = await agendaAPI.testIcal(selectedSchedule.value.id);
    icalTestResult.value = res.data;
  } catch (e) {
    icalTestResult.value = {
      ok: false,
      error: e.response?.data?.error || 'Erro ao testar. Verifique a URL.',
    };
  } finally {
    isTestingIcal.value = false;
  }
};

const doDisconnectGoogle = async () => {
  if (!selectedSchedule.value) return;
  if (!confirm('Desconectar o Google Agenda desta agenda?')) return;
  try {
    await agendaAPI.disconnectGoogleCalendar(selectedSchedule.value.id);
    // Refresh the schedule to clear google_connected_email
    const res = await agendaAPI.show(selectedSchedule.value.id);
    const updated = res.data?.payload || res.data;
    const idx = schedules.value.findIndex(s => s.id === updated.id);
    if (idx >= 0) schedules.value[idx] = updated;
    useAlert('Google Agenda desconectado.');
  } catch {
    useAlert('Não foi possível desconectar.');
  }
};

// ─── Helpers ──────────────────────────────────────────────
const statusConfig = status => STATUS_CONFIG[status] || STATUS_CONFIG.confirmed;

// Inline CSS color for slot dots/bar (avoids Tailwind purge of dynamic classes)
const slotFillColor = slot => {
  const total = (slot.reserved_slots || 0) + (slot.remaining_slots || 0);
  if (!total || slot.status !== 'available') return '#94a3b8'; // slate-400
  const ratio = (slot.reserved_slots || 0) / total;
  if (ratio < 0.5) return '#34d399'; // emerald-400
  if (ratio < 0.75) return '#fbbf24'; // amber-400
  return '#f97316'; // orange-500
};

// Returns occupation bar width string for a slot
const slotOccupationWidth = slot => {
  const total = (slot.reserved_slots || 0) + (slot.remaining_slots || 0);
  return total > 0 ? `${((slot.reserved_slots || 0) / total) * 100}%` : '0%';
};

const kpiRate = (a, b) => {
  if (!b) return 0;
  return Math.round((a / b) * 100);
};

// ─── KPI derived computeds ────────────────────────────────

const attendanceRate = computed(() =>
  stats.value
    ? kpiRate(
        stats.value.completed,
        stats.value.confirmed + stats.value.completed
      )
    : 0
);
const cancellationRate = computed(() =>
  stats.value ? kpiRate(stats.value.cancelled, stats.value.total) : 0
);
const noShowRate = computed(() =>
  stats.value
    ? kpiRate(stats.value.no_show, stats.value.completed + stats.value.no_show)
    : 0
);
const confirmationRate = computed(() =>
  stats.value
    ? kpiRate(
        stats.value.confirmed,
        stats.value.confirmed + stats.value.pending
      )
    : 0
);

const upcomingBookings = computed(() =>
  statsBookings.value
    .filter(b => {
      try {
        return parseISO(b.starts_at) > new Date() && b.status !== 'cancelled';
      } catch {
        return false;
      }
    })
    .sort((a, b) => parseISO(a.starts_at) - parseISO(b.starts_at))
    .slice(0, 10)
);

const bookingsByDow = computed(() => {
  const DAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  const counts = [0, 0, 0, 0, 0, 0, 0];
  statsBookings.value.forEach(b => {
    try {
      counts[parseISO(b.starts_at).getDay()]++;
    } catch {
      /* skip */
    }
  });
  const max = Math.max(...counts, 1);
  return DAYS.map((day, i) => ({
    day,
    count: counts[i],
    pct: Math.round((counts[i] / max) * 100),
  }));
});

const bookingsByHour = computed(() => {
  const counts = {};
  statsBookings.value.forEach(b => {
    try {
      const h = parseISO(b.starts_at).getHours();
      counts[h] = (counts[h] || 0) + 1;
    } catch {
      /* skip */
    }
  });
  if (!Object.keys(counts).length) return [];
  const max = Math.max(...Object.values(counts), 1);
  return Object.entries(counts)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([h, count]) => ({
      hour: `${String(h).padStart(2, '0')}:00`,
      count,
      pct: Math.round((count / max) * 100),
    }));
});

const statusDistribution = computed(() => {
  if (!stats.value) return [];
  const total = stats.value.total || 1;
  return [
    {
      key: 'confirmed',
      label: 'Confirmados',
      value: stats.value.confirmed,
      color: '#10b981',
    },
    {
      key: 'pending',
      label: 'Pendentes',
      value: stats.value.pending,
      color: '#f59e0b',
    },
    {
      key: 'completed',
      label: 'Realizados',
      value: stats.value.completed,
      color: '#3b82f6',
    },
    {
      key: 'cancelled',
      label: 'Cancelados',
      value: stats.value.cancelled,
      color: '#ef4444',
    },
    {
      key: 'no_show',
      label: 'No-show',
      value: stats.value.no_show,
      color: '#94a3b8',
    },
  ].map(s => ({ ...s, pct: Math.round((s.value / total) * 100) }));
});

const avgSlotQty = computed(() => {
  if (!statsBookings.value.length) return '—';
  const sum = statsBookings.value.reduce(
    (acc, b) => acc + (b.slot_quantity || 1),
    0
  );
  return (sum / statsBookings.value.length).toFixed(1);
});

const bookingsByType = computed(() => {
  const map = {};
  statsBookings.value.forEach(b => {
    const key = b.metadata?.appointment_type || '(sem tipo)';
    if (!map[key])
      map[key] = {
        label: key,
        total: 0,
        completed: 0,
        confirmed: 0,
        cancelled: 0,
        no_show: 0,
        upcoming: 0,
      };
    map[key].total++;
    if (b.status === 'completed') map[key].completed++;
    if (b.status === 'confirmed') map[key].confirmed++;
    if (b.status === 'cancelled') map[key].cancelled++;
    if (b.status === 'no_show') map[key].no_show++;
    try {
      if (parseISO(b.starts_at) > new Date() && b.status !== 'cancelled')
        map[key].upcoming++;
    } catch {
      /* skip */
    }
  });
  return Object.values(map).sort((a, b) => b.total - a.total);
});

const bookingsByProduct = computed(() => {
  const map = {};
  statsBookings.value.forEach(b => {
    const products = bookingProducts(b);
    const keys = products.length > 0 ? products : ['(sem produto)'];
    keys.forEach(key => {
      if (!map[key])
        map[key] = {
          label: key,
          total: 0,
          completed: 0,
          confirmed: 0,
          cancelled: 0,
          no_show: 0,
          upcoming: 0,
        };
      map[key].total++;
      if (b.status === 'completed') map[key].completed++;
      if (b.status === 'confirmed') map[key].confirmed++;
      if (b.status === 'cancelled') map[key].cancelled++;
      if (b.status === 'no_show') map[key].no_show++;
      try {
        if (parseISO(b.starts_at) > new Date() && b.status !== 'cancelled')
          map[key].upcoming++;
      } catch {
        /* skip */
      }
    });
  });
  return Object.values(map).sort((a, b) => b.total - a.total);
});

// ─── Lifecycle ────────────────────────────────────────────
onMounted(async () => {
  await loadAll();
  setInterval(() => {
    now.value = new Date();
  }, 60_000);

  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('calendar_connected') === '1') {
    const scheduleId = urlParams.get('schedule_id');
    if (scheduleId) {
      const res = await agendaAPI.show(scheduleId);
      const updated = res.data?.payload || res.data;
      const idx = schedules.value.findIndex(s => s.id === updated.id);
      if (idx >= 0) schedules.value[idx] = updated;
      selectedScheduleId.value = updated.id;
    }
    activeTab.value = 'settings';
    useAlert('Google Agenda conectado com sucesso!');
    window.history.replaceState({}, '', window.location.pathname);
  } else if (urlParams.get('calendar_error') === '1') {
    useAlert('Falha ao conectar com o Google Agenda. Tente novamente.');
    window.history.replaceState({}, '', window.location.pathname);
  }
});
</script>

<template>
  <FeatureLockedScreen v-if="!hasAgenda" feature-key="agenda" />
  <div
    v-else
    class="flex flex-col h-full w-full bg-n-background overflow-hidden"
  >
    <!-- ═══════════════════════════════════════════════════
         HEADER
    ═══════════════════════════════════════════════════ -->
    <header class="flex-shrink-0 border-b border-n-weak bg-n-background">
      <!-- Top row: title + schedule selector + actions -->
      <div class="flex items-center gap-3 px-6 py-3">
        <div class="flex items-center gap-2.5 flex-shrink-0">
          <div
            class="flex items-center justify-center w-8 h-8 rounded-xl"
            :style="
              selectedSchedule
                ? { backgroundColor: selectedSchedule.color + '22' }
                : { backgroundColor: '#6366f122' }
            "
          >
            <span
              class="i-lucide-calendar-range size-4"
              :style="
                selectedSchedule
                  ? { color: selectedSchedule.color }
                  : { color: '#6366f1' }
              "
            />
          </div>
          <h1 class="text-sm font-semibold text-n-slate-12">Agenda</h1>
        </div>

        <div class="w-px h-5 bg-n-weak flex-shrink-0" />

        <!-- Schedule selector -->
        <div class="flex-1 flex items-center gap-2 min-w-0">
          <select
            v-model="selectedScheduleId"
            class="flex-1 max-w-xs px-3 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all truncate"
          >
            <option v-if="schedules.length === 0" :value="null">
              Nenhuma agenda criada
            </option>
            <option v-for="s in schedules" :key="s.id" :value="s.id">
              {{ s.title }}
            </option>
          </select>

          <!-- Active badge -->
          <span
            v-if="selectedSchedule"
            class="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border"
            :class="
              selectedSchedule.active
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-n-alpha-1 text-n-slate-9 border-n-weak'
            "
          >
            <span
              class="w-1.5 h-1.5 rounded-full"
              :class="
                selectedSchedule.active ? 'bg-emerald-500' : 'bg-n-slate-6'
              "
            />
            {{ selectedSchedule.active ? 'Ativo' : 'Inativo' }}
          </span>

          <!-- Schedule mini info -->
          <span
            v-if="selectedSchedule"
            class="hidden md:flex items-center gap-1.5 text-xs text-n-slate-8"
          >
            <span class="i-lucide-clock size-3" />
            {{ selectedSchedule.duration_minutes }}min
            <span class="i-lucide-users size-3 ml-1" />
            {{ selectedSchedule.slot_capacity }} vaga{{
              selectedSchedule.slot_capacity !== 1 ? 's' : ''
            }}
          </span>
        </div>

        <!-- Action buttons -->
        <div class="flex items-center gap-1.5 flex-shrink-0">
          <button
            v-if="selectedSchedule"
            class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2 rounded-lg transition-colors"
            title="Bloquear horário"
            @click="openBlockModal()"
          >
            <span class="i-lucide-ban size-3.5" />
            <span class="hidden md:inline">Bloquear horário</span>
          </button>

          <button
            v-if="selectedSchedule"
            class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2 rounded-lg transition-colors"
            title="Novo agendamento"
            @click="openNewBooking()"
          >
            <span class="i-lucide-calendar-plus size-3.5" />
            <span class="hidden md:inline">Novo agendamento</span>
          </button>

          <button
            class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2 rounded-lg transition-colors border border-n-weak"
            title="Cadastro rápido de contato"
            @click="openQuickContactModal"
          >
            <span class="i-lucide-user-plus size-3.5" />
            <span class="hidden md:inline">Novo contato</span>
          </button>

          <button
            class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-n-brand text-white rounded-lg hover:opacity-90 transition-all"
            @click="openNewSchedule"
          >
            <span class="i-lucide-plus size-3.5" />
            <span class="hidden md:inline">Nova agenda</span>
          </button>
        </div>
      </div>

      <!-- Tab navigation -->
      <nav class="flex items-center gap-0.5 px-6 overflow-x-auto no-scrollbar">
        <button
          v-for="tab in TABS"
          :key="tab.key"
          class="flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-all whitespace-nowrap"
          :class="
            activeTab === tab.key
              ? 'border-n-brand text-n-brand'
              : 'border-transparent text-n-slate-9 hover:text-n-slate-12 hover:border-n-weak'
          "
          @click="activeTab = tab.key"
        >
          <span class="size-3.5" :class="[tab.icon]" />
          {{ tab.label }}
        </button>
      </nav>
    </header>

    <!-- ═══════════════════════════════════════════════════
         EMPTY STATE — no schedules
    ═══════════════════════════════════════════════════ -->
    <div
      v-if="!isLoading && schedules.length === 0"
      class="flex-1 flex flex-col items-center justify-center gap-4 p-8"
    >
      <div
        class="w-16 h-16 rounded-2xl bg-n-alpha-1 flex items-center justify-center"
      >
        <span class="i-lucide-calendar-range size-8 text-n-slate-7" />
      </div>
      <div class="text-center">
        <h2 class="text-base font-semibold text-n-slate-12">
          Nenhuma agenda criada
        </h2>
        <p class="text-sm text-n-slate-9 mt-1">
          Crie sua primeira agenda para começar a gerenciar agendamentos.
        </p>
      </div>
      <button
        class="flex items-center gap-2 px-5 py-2.5 bg-n-brand text-white text-sm font-medium rounded-xl hover:opacity-90 transition-all"
        @click="openNewSchedule"
      >
        <span class="i-lucide-plus size-4" />
        Criar primeira agenda
      </button>
    </div>

    <!-- ═══════════════════════════════════════════════════
         MAIN CONTENT
    ═══════════════════════════════════════════════════ -->
    <main v-else class="flex-1 w-full overflow-hidden">
      <!-- ─────────────────────────────────────────────────
           TAB: CALENDÁRIO
      ───────────────────────────────────────────────── -->
      <div v-if="activeTab === 'calendar'" class="flex h-full overflow-hidden">
        <!-- Left: Schedule list sidebar -->
        <aside
          class="w-72 flex-shrink-0 border-r border-n-weak flex flex-col overflow-y-auto"
        >
          <!-- Calendar navigation controls -->
          <div class="flex items-center gap-1 p-3 border-b border-n-weak">
            <button
              class="p-1.5 rounded-lg hover:bg-n-alpha-2 text-n-slate-9 transition-colors"
              @click="navigate(-1)"
            >
              <span class="i-lucide-chevron-left size-3.5" />
            </button>
            <span
              class="flex-1 text-center text-xs font-semibold text-n-slate-12 truncate"
            >
              {{ calendarTitle }}
            </span>
            <button
              class="p-1.5 rounded-lg hover:bg-n-alpha-2 text-n-slate-9 transition-colors"
              @click="navigate(1)"
            >
              <span class="i-lucide-chevron-right size-3.5" />
            </button>
          </div>

          <!-- View toggle + Today -->
          <div class="flex items-center gap-1 p-3 border-b border-n-weak">
            <button
              class="flex-1 py-1 text-xs font-medium rounded-lg transition-all"
              :class="
                calendarView === 'month'
                  ? 'bg-n-brand text-white'
                  : 'text-n-slate-9 hover:bg-n-alpha-2'
              "
              @click="calendarView = 'month'"
            >
              Mês
            </button>
            <button
              class="flex-1 py-1 text-xs font-medium rounded-lg transition-all"
              :class="
                calendarView === 'week'
                  ? 'bg-n-brand text-white'
                  : 'text-n-slate-9 hover:bg-n-alpha-2'
              "
              @click="calendarView = 'week'"
            >
              Semana
            </button>
            <button
              class="flex-1 py-1 text-xs font-medium rounded-lg transition-all"
              :class="
                calendarView === 'day'
                  ? 'bg-n-brand text-white'
                  : 'text-n-slate-9 hover:bg-n-alpha-2'
              "
              @click="calendarView = 'day'"
            >
              Dia
            </button>
            <button
              class="ml-1 px-2.5 py-1 text-xs font-medium text-n-brand hover:bg-n-brand/10 rounded-lg transition-all"
              @click="goToday"
            >
              Hoje
            </button>
          </div>

          <!-- All schedules list -->
          <div class="p-3 space-y-1.5">
            <p
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wide mb-2"
            >
              Agendas
            </p>
            <button
              v-for="s in schedules"
              :key="s.id"
              class="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border transition-all text-left"
              :class="
                selectedScheduleId === s.id
                  ? 'border-transparent shadow-sm'
                  : 'border-n-weak hover:border-n-slate-5'
              "
              :style="
                selectedScheduleId === s.id
                  ? {
                      backgroundColor: s.color + '18',
                      borderColor: s.color + '40',
                    }
                  : {}
              "
              @click="selectedScheduleId = s.id"
            >
              <span
                class="flex-shrink-0 w-2.5 h-2.5 rounded-full"
                :style="{ backgroundColor: s.color }"
              />
              <div class="flex-1 min-w-0">
                <div class="text-xs font-semibold text-n-slate-12 truncate">
                  {{ s.title }}
                </div>
                <div class="text-xs text-n-slate-8 truncate">
                  {{ s.duration_minutes }}min · {{ s.slot_capacity }} vaga{{
                    s.slot_capacity !== 1 ? 's' : ''
                  }}
                </div>
              </div>
              <span
                class="flex-shrink-0 w-1.5 h-1.5 rounded-full"
                :class="s.active ? 'bg-emerald-400' : 'bg-n-slate-5'"
              />
            </button>
          </div>

          <!-- Selected day panel -->
          <div v-if="selectedSchedule" class="border-t border-n-weak">
            <div class="p-3">
              <div class="flex items-center justify-between mb-3">
                <div>
                  <p class="text-xs font-semibold text-n-slate-12">
                    {{ format(selectedDate, 'dd') }} de
                    {{ MONTHS_PT[selectedDate.getMonth()] }}
                  </p>
                  <p class="text-xs text-n-slate-8">
                    {{ daySlots.filter(s => s.status === 'available').length }}
                    vagas · {{ dayBookings.length }} agendamentos
                  </p>
                </div>
                <button
                  class="flex items-center gap-1 px-2.5 py-1.5 bg-n-brand text-white text-xs font-medium rounded-lg hover:opacity-90 transition-all"
                  @click="openNewBooking()"
                >
                  <span class="i-lucide-plus size-3" />
                  Novo
                </button>
              </div>

              <!-- Day slots -->
              <div class="space-y-1.5 max-h-48 overflow-y-auto">
                <div
                  v-for="slot in daySlots"
                  :key="slot.starts_at"
                  class="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs cursor-pointer transition-all hover:shadow-sm"
                  :class="
                    slot.status === 'available'
                      ? 'border-emerald-200 bg-emerald-50/50 hover:bg-emerald-50'
                      : 'border-n-weak bg-n-alpha-1 opacity-60'
                  "
                  @click="openSlotDetail(slot)"
                >
                  <span
                    class="font-semibold text-n-slate-12 w-10 flex-shrink-0"
                  >
                    {{ format(parseISO(slot.starts_at), 'HH:mm') }}
                  </span>
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-1">
                      <span
                        v-for="n in slot.remaining_slots"
                        :key="n"
                        class="w-1.5 h-1.5 rounded-full"
                        :style="{ backgroundColor: slotFillColor(slot) }"
                      />
                      <span
                        v-for="n in slot.reserved_slots || 0"
                        :key="'r' + n"
                        class="w-1.5 h-1.5 rounded-full bg-n-slate-5"
                      />
                    </div>
                  </div>
                  <span
                    class="text-xs font-medium"
                    :class="
                      slot.status === 'available'
                        ? 'text-emerald-600'
                        : 'text-n-slate-7'
                    "
                  >
                    {{ slot.remaining_slots }}
                  </span>
                </div>

                <p
                  v-if="daySlots.length === 0"
                  class="text-xs text-n-slate-8 text-center py-2"
                >
                  Sem slots para este dia.
                </p>
              </div>

              <!-- Day bookings -->
              <div v-if="dayBookings.length > 0" class="mt-3 space-y-1.5">
                <p
                  class="text-xs font-semibold text-n-slate-9 uppercase tracking-wide"
                >
                  Agendamentos
                </p>
                <div
                  v-for="booking in dayBookings"
                  :key="booking.id"
                  class="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs"
                  :class="[
                    statusConfig(booking.status).classes,
                    isBookingNow(booking) ? 'ring-1 ring-red-400' : '',
                    !isBookingNow(booking) && booking.id === nextDayBookingId
                      ? 'ring-1 ring-yellow-400'
                      : '',
                  ]"
                >
                  <span class="font-semibold flex-shrink-0">
                    {{ format(parseISO(booking.starts_at), 'HH:mm') }}
                  </span>
                  <span class="flex-1 truncate">{{
                    booking.attendee_name
                  }}</span>
                  <span
                    v-if="bookingProducts(booking).length > 0"
                    class="flex-shrink-0 text-[10px] opacity-70 max-w-[80px] truncate"
                  >
                    {{ bookingProducts(booking).join(', ') }}
                  </span>
                  <span
                    v-if="booking.metadata?.appointment_type"
                    class="flex-shrink-0 text-[10px] opacity-70 max-w-[60px] truncate"
                  >
                    {{ booking.metadata.appointment_type }}
                  </span>
                  <span
                    v-if="isBookingNow(booking)"
                    class="inline-flex items-center gap-0.5 px-1 py-0 rounded text-[9px] font-bold bg-red-600 text-white flex-shrink-0"
                  >
                    <span class="animate-pulse w-1 h-1 rounded-full bg-white" />
                    AO VIVO
                  </span>
                  <span
                    v-else-if="booking.id === nextDayBookingId"
                    class="inline-flex items-center gap-0.5 px-1 py-0 rounded text-[9px] font-bold bg-yellow-300 text-yellow-900 border border-yellow-500 flex-shrink-0"
                  >
                    PRÓX
                  </span>
                </div>
              </div>
            </div>
          </div>
        </aside>

        <!-- Right: Calendar main area -->
        <section class="flex-1 relative overflow-hidden">
          <AgendaCalendar
            :view="calendarView"
            :anchor-date="anchorDate"
            :selected-date="selectedDate"
            :bookings="bookings"
            :availability="availability"
            :schedule-color="selectedSchedule?.color || '#6366f1'"
            :is-loading="isLoading"
            @select-date="
              day => {
                selectedDate = day;
                anchorDate = day;
                if (calendarView === 'month') calendarView = 'day';
              }
            "
            @select-slot="slot => openNewBooking(slot)"
            @create-booking="openNewBooking()"
            @move-booking="moveBooking"
            @edit-booking="openEditBooking"
          />
        </section>
      </div>

      <!-- ─────────────────────────────────────────────────
           TAB: AGENDAMENTOS
      ───────────────────────────────────────────────── -->
      <div
        v-else-if="activeTab === 'bookings'"
        class="flex flex-col h-full overflow-hidden"
      >
        <!-- Filter bar -->
        <div
          class="flex flex-col border-b border-n-weak bg-n-background flex-shrink-0"
        >
          <!-- Row 1: period + new button -->
          <div class="flex items-center gap-2 px-6 pt-3 pb-2">
            <span class="text-xs font-semibold text-n-slate-9 flex-shrink-0">Período:</span>
            <div class="flex items-center gap-1 flex-wrap">
              <button
                v-for="p in [
                  { key: 'today', label: 'Hoje' },
                  { key: 'week', label: 'Semana' },
                  { key: 'month', label: 'Mês' },
                  { key: 'custom', label: 'Personalizado' },
                ]"
                :key="p.key"
                class="px-3 py-1 text-xs font-medium rounded-lg transition-all"
                :class="
                  bookingPeriod === p.key
                    ? 'bg-n-brand text-white'
                    : 'text-n-slate-9 hover:bg-n-alpha-2 border border-n-weak'
                "
                @click="bookingPeriod = p.key"
              >
                {{ p.label }}
              </button>
            </div>
            <template v-if="bookingPeriod === 'custom'">
              <input
                v-model="listCustomStart"
                type="date"
                class="px-2 py-1 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
              />
              <span class="text-xs text-n-slate-8">até</span>
              <input
                v-model="listCustomEnd"
                type="date"
                class="px-2 py-1 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
              />
            </template>
            <span
              v-if="isLoadingList"
              class="i-lucide-loader-2 size-3.5 animate-spin text-n-slate-8"
            />
            <button
              class="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-n-brand text-white text-xs font-medium rounded-lg hover:opacity-90 transition-all"
              @click="openNewBooking()"
            >
              <span class="i-lucide-plus size-3.5" />
              Novo
            </button>
          </div>

          <!-- Row 2: search + status filter -->
          <div class="flex items-center gap-3 px-6 pb-3">
            <div class="relative flex-1 max-w-xs">
              <span
                class="absolute left-3 top-1/2 -translate-y-1/2 i-lucide-search size-3.5 text-n-slate-8"
              />
              <input
                v-model="bookingSearch"
                type="text"
                placeholder="Buscar participante, produto, tipo ou profissional..."
                class="w-full pl-8 pr-3 py-2 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 placeholder-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all"
              />
            </div>

            <div class="flex items-center gap-1">
              <button
                v-for="(cfg, key) in {
                  all: 'Todos',
                  ...Object.fromEntries(
                    Object.entries(STATUS_CONFIG).map(([k, v]) => [k, v.label])
                  ),
                }"
                :key="key"
                class="px-3 py-1.5 text-xs font-medium rounded-lg transition-all"
                :class="
                  bookingFilter === key
                    ? 'bg-n-brand text-white'
                    : 'text-n-slate-9 hover:bg-n-alpha-2'
                "
                @click="bookingFilter = key"
              >
                {{ cfg }}
              </button>
            </div>
          </div>
        </div>

        <!-- Bookings table -->
        <div class="flex-1 overflow-y-auto">
          <div
            v-if="filteredListBookings.length === 0 && !isLoadingList"
            class="flex flex-col items-center justify-center h-full gap-3 p-8"
          >
            <span class="i-lucide-calendar-x size-10 text-n-slate-6" />
            <p class="text-sm text-n-slate-9">Nenhum agendamento encontrado.</p>
          </div>

          <table v-else class="w-full text-sm">
            <thead
              class="sticky top-0 bg-n-background border-b border-n-weak z-10"
            >
              <tr>
                <th
                  class="text-left px-6 py-3 text-xs font-semibold text-n-slate-9 uppercase tracking-wide w-0"
                />
                <th
                  class="text-left px-4 py-3 text-xs font-semibold text-n-slate-9 uppercase tracking-wide whitespace-nowrap"
                >
                  Participante
                </th>
                <th
                  class="text-left px-4 py-3 text-xs font-semibold text-n-slate-9 uppercase tracking-wide whitespace-nowrap"
                >
                  Data e hora
                </th>

                <!-- Produto column with Excel-style filter -->
                <th class="text-left px-4 py-3 relative">
                  <div class="flex items-center gap-1 whitespace-nowrap">
                    <span
                      class="text-xs font-semibold text-n-slate-9 uppercase tracking-wide"
                      >Produto</span>
                    <button
                      class="flex items-center justify-center w-5 h-5 rounded transition-colors flex-shrink-0"
                      :class="
                        bookingProductFilter
                          ? 'bg-yellow-300 text-yellow-900 border border-yellow-500'
                          : 'text-n-slate-6 hover:bg-n-alpha-2 hover:text-n-slate-11'
                      "
                      title="Filtrar por produto"
                      @click.stop="
                        showProductFilter = !showProductFilter;
                        showResourceFilter = false;
                        showTypeFilter = false;
                      "
                    >
                      <span class="i-lucide-list-filter size-3" />
                    </button>
                  </div>
                  <!-- Dropdown -->
                  <div
                    v-if="showProductFilter"
                    class="absolute top-full left-0 mt-1 z-50 bg-n-background border border-n-weak rounded-xl shadow-2xl py-1 min-w-[160px]"
                    @click.stop
                  >
                    <div class="px-3 py-1.5 border-b border-n-weak mb-1">
                      <span
                        class="text-[10px] font-semibold text-n-slate-8 uppercase tracking-wide"
                        >Filtrar produto</span>
                    </div>
                    <button
                      class="w-full text-left px-3 py-2 text-xs transition-colors hover:bg-n-alpha-1 flex items-center gap-2"
                      :class="
                        !bookingProductFilter
                          ? 'text-n-brand font-semibold'
                          : 'text-n-slate-11'
                      "
                      @click="
                        bookingProductFilter = '';
                        showProductFilter = false;
                      "
                    >
                      <span
                        class="w-3 h-3 rounded-full border border-n-weak flex-shrink-0"
                        :class="
                          !bookingProductFilter
                            ? 'bg-n-brand border-n-brand'
                            : ''
                        "
                      />
                      Todos
                    </button>
                    <button
                      v-for="p in uniqueListProducts"
                      :key="p"
                      class="w-full text-left px-3 py-2 text-xs transition-colors hover:bg-n-alpha-1 flex items-center gap-2"
                      :class="
                        bookingProductFilter === p
                          ? 'text-n-brand font-semibold'
                          : 'text-n-slate-11'
                      "
                      @click="
                        bookingProductFilter = p;
                        showProductFilter = false;
                      "
                    >
                      <span
                        class="w-3 h-3 rounded-full border border-n-weak flex-shrink-0"
                        :class="
                          bookingProductFilter === p
                            ? 'bg-n-brand border-n-brand'
                            : ''
                        "
                      />
                      {{ p }}
                    </button>
                    <div
                      v-if="!uniqueListProducts.length"
                      class="px-3 py-2 text-xs text-n-slate-7 italic"
                    >
                      Nenhum produto no período
                    </div>
                  </div>
                </th>

                <!-- Profissional column with Excel-style filter -->
                <th class="text-left px-4 py-3 relative">
                  <div class="flex items-center gap-1 whitespace-nowrap">
                    <span
                      class="text-xs font-semibold text-n-slate-9 uppercase tracking-wide"
                      >Profissional</span>
                    <button
                      class="flex items-center justify-center w-5 h-5 rounded transition-colors flex-shrink-0"
                      :class="
                        bookingResourceFilter
                          ? 'bg-yellow-300 text-yellow-900 border border-yellow-500'
                          : 'text-n-slate-6 hover:bg-n-alpha-2 hover:text-n-slate-11'
                      "
                      title="Filtrar por profissional"
                      @click.stop="
                        showResourceFilter = !showResourceFilter;
                        showProductFilter = false;
                        showTypeFilter = false;
                      "
                    >
                      <span class="i-lucide-list-filter size-3" />
                    </button>
                  </div>
                  <!-- Dropdown -->
                  <div
                    v-if="showResourceFilter"
                    class="absolute top-full left-0 mt-1 z-50 bg-n-background border border-n-weak rounded-xl shadow-2xl py-1 min-w-[160px]"
                    @click.stop
                  >
                    <div class="px-3 py-1.5 border-b border-n-weak mb-1">
                      <span
                        class="text-[10px] font-semibold text-n-slate-8 uppercase tracking-wide"
                        >Filtrar profissional</span>
                    </div>
                    <button
                      class="w-full text-left px-3 py-2 text-xs transition-colors hover:bg-n-alpha-1 flex items-center gap-2"
                      :class="
                        !bookingResourceFilter
                          ? 'text-n-brand font-semibold'
                          : 'text-n-slate-11'
                      "
                      @click="
                        bookingResourceFilter = '';
                        showResourceFilter = false;
                      "
                    >
                      <span
                        class="w-3 h-3 rounded-full border border-n-weak flex-shrink-0"
                        :class="
                          !bookingResourceFilter
                            ? 'bg-n-brand border-n-brand'
                            : ''
                        "
                      />
                      Todos
                    </button>
                    <button
                      v-for="r in uniqueListResources"
                      :key="r.id"
                      class="w-full text-left px-3 py-2 text-xs transition-colors hover:bg-n-alpha-1 flex items-center gap-2"
                      :class="
                        bookingResourceFilter === String(r.id)
                          ? 'text-n-brand font-semibold'
                          : 'text-n-slate-11'
                      "
                      @click="
                        bookingResourceFilter = String(r.id);
                        showResourceFilter = false;
                      "
                    >
                      <span
                        class="w-3 h-3 rounded-full flex-shrink-0 border"
                        :class="
                          bookingResourceFilter === String(r.id)
                            ? 'bg-n-brand border-n-brand'
                            : 'border-n-weak'
                        "
                      />
                      {{ r.name }}
                    </button>
                    <div
                      v-if="!uniqueListResources.length"
                      class="px-3 py-2 text-xs text-n-slate-7 italic"
                    >
                      Nenhum profissional no período
                    </div>
                  </div>
                </th>

                <!-- Tipo column with Excel-style filter -->
                <th class="text-left px-4 py-3 relative">
                  <div class="flex items-center gap-1 whitespace-nowrap">
                    <span
                      class="text-xs font-semibold text-n-slate-9 uppercase tracking-wide"
                      >Tipo</span>
                    <button
                      class="flex items-center justify-center w-5 h-5 rounded transition-colors flex-shrink-0"
                      :class="
                        bookingTypeFilter
                          ? 'bg-yellow-300 text-yellow-900 border border-yellow-500'
                          : 'text-n-slate-6 hover:bg-n-alpha-2 hover:text-n-slate-11'
                      "
                      title="Filtrar por tipo"
                      @click.stop="
                        showTypeFilter = !showTypeFilter;
                        showProductFilter = false;
                        showResourceFilter = false;
                      "
                    >
                      <span class="i-lucide-list-filter size-3" />
                    </button>
                  </div>
                  <!-- Dropdown -->
                  <div
                    v-if="showTypeFilter"
                    class="absolute top-full left-0 mt-1 z-50 bg-n-background border border-n-weak rounded-xl shadow-2xl py-1 min-w-[160px]"
                    @click.stop
                  >
                    <div class="px-3 py-1.5 border-b border-n-weak mb-1">
                      <span
                        class="text-[10px] font-semibold text-n-slate-8 uppercase tracking-wide"
                        >Filtrar tipo</span>
                    </div>
                    <button
                      class="w-full text-left px-3 py-2 text-xs transition-colors hover:bg-n-alpha-1 flex items-center gap-2"
                      :class="
                        !bookingTypeFilter
                          ? 'text-n-brand font-semibold'
                          : 'text-n-slate-11'
                      "
                      @click="
                        bookingTypeFilter = '';
                        showTypeFilter = false;
                      "
                    >
                      <span
                        class="w-3 h-3 rounded-full border border-n-weak flex-shrink-0"
                        :class="
                          !bookingTypeFilter ? 'bg-n-brand border-n-brand' : ''
                        "
                      />
                      Todos
                    </button>
                    <button
                      v-for="t in uniqueListTypes"
                      :key="t"
                      class="w-full text-left px-3 py-2 text-xs transition-colors hover:bg-n-alpha-1 flex items-center gap-2"
                      :class="
                        bookingTypeFilter === t
                          ? 'text-n-brand font-semibold'
                          : 'text-n-slate-11'
                      "
                      @click="
                        bookingTypeFilter = t;
                        showTypeFilter = false;
                      "
                    >
                      <span
                        class="w-3 h-3 rounded-full border border-n-weak flex-shrink-0"
                        :class="
                          bookingTypeFilter === t
                            ? 'bg-n-brand border-n-brand'
                            : ''
                        "
                      />
                      {{ t }}
                    </button>
                    <div
                      v-if="!uniqueListTypes.length"
                      class="px-3 py-2 text-xs text-n-slate-7 italic"
                    >
                      Nenhum tipo no período
                    </div>
                  </div>
                </th>

                <th
                  class="text-left px-4 py-3 text-xs font-semibold text-n-slate-9 uppercase tracking-wide whitespace-nowrap"
                >
                  Valor
                </th>
                <th
                  class="text-left px-4 py-3 text-xs font-semibold text-n-slate-9 uppercase tracking-wide whitespace-nowrap"
                >
                  Origem
                </th>
                <th
                  class="text-left px-4 py-3 text-xs font-semibold text-n-slate-9 uppercase tracking-wide whitespace-nowrap"
                >
                  Status
                </th>
                <th class="px-4 py-3" />
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr
                v-for="booking in filteredListBookings"
                :key="booking.id"
                class="hover:bg-n-alpha-1 transition-colors cursor-pointer border-l-[3px]"
                :class="[
                  isBookingPast(booking) && !isBookingNow(booking)
                    ? 'opacity-45'
                    : '',
                  isBookingNow(booking) ? 'bg-red-50/40' : '',
                  !isBookingNow(booking) && booking.id === nextListBookingId
                    ? 'bg-yellow-50/70'
                    : '',
                ]"
                :style="{
                  borderLeftColor:
                    isBookingPast(booking) && !isBookingNow(booking)
                      ? '#94a3b8'
                      : STATUS_COLOR[booking.status] || '#94a3b8',
                }"
                @click="openEditBooking(booking)"
              >
                <td class="w-0 p-0" />

                <td class="px-4 py-3">
                  <div class="font-medium text-n-slate-12">
                    {{ booking.attendee_name }}
                  </div>
                  <div class="text-xs text-n-slate-8 mt-0.5">
                    {{
                      booking.attendee_email || booking.attendee_phone || '—'
                    }}
                  </div>
                </td>

                <td class="px-4 py-3 min-w-[120px]">
                  <div class="flex items-center gap-2">
                    <span class="font-semibold text-n-slate-12">
                      {{ format(parseISO(booking.starts_at), 'dd/MM') }}
                    </span>
                    <span
                      v-if="isBookingNow(booking)"
                      class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-600 text-white leading-none"
                    >
                      <span
                        class="animate-pulse w-1.5 h-1.5 rounded-full bg-white flex-shrink-0"
                      />
                      AO VIVO
                    </span>
                    <span
                      v-else-if="booking.id === nextListBookingId"
                      class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-yellow-300 text-yellow-900 border border-yellow-500 leading-none"
                    >
                      <span class="i-lucide-arrow-right size-2.5" />
                      PRÓXIMO
                    </span>
                  </div>
                  <div
                    class="text-xs font-semibold mt-0.5"
                    :class="
                      isBookingPast(booking) && !isBookingNow(booking)
                        ? 'text-n-slate-7'
                        : 'text-n-slate-11'
                    "
                  >
                    {{ format(parseISO(booking.starts_at), 'HH:mm') }} –
                    {{ format(parseISO(booking.ends_at), 'HH:mm') }}
                  </div>
                </td>

                <td class="px-4 py-3">
                  <div
                    v-if="bookingProducts(booking).length > 0"
                    class="flex flex-wrap gap-1"
                  >
                    <span
                      v-for="p in bookingProducts(booking)"
                      :key="p"
                      class="inline-flex items-center px-2 py-0.5 bg-n-alpha-2 border border-n-weak rounded-lg text-xs text-n-slate-10"
                      >{{ p }}</span>
                  </div>
                  <span v-else class="text-n-slate-6 text-xs">—</span>
                </td>

                <td class="px-4 py-3">
                  <span
                    v-if="booking.resource"
                    class="flex items-center gap-1.5"
                  >
                    <span
                      class="w-5 h-5 rounded-full flex items-center justify-center text-xs font-semibold text-white flex-shrink-0"
                      :style="{
                        backgroundColor: booking.resource.color || '#6366f1',
                      }"
                    >
                      {{ booking.resource.name[0] }}
                    </span>
                    <span class="text-n-slate-11 text-xs">{{
                      booking.resource.name
                    }}</span>
                  </span>
                  <span v-else class="text-n-slate-7 text-xs">—</span>
                </td>

                <td class="px-4 py-3">
                  <span
                    v-if="booking.metadata?.appointment_type"
                    class="inline-flex items-center px-2 py-0.5 bg-n-alpha-2 border border-n-weak rounded-lg text-xs text-n-slate-10"
                  >
                    {{ booking.metadata.appointment_type }}
                  </span>
                  <span v-else class="text-n-slate-6 text-xs">—</span>
                </td>

                <td class="px-4 py-3">
                  <span
                    v-if="booking.sale_value != null"
                    class="text-sm font-medium text-n-slate-12"
                  >
                    R$
                    {{
                      Number(booking.sale_value).toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                      })
                    }}
                  </span>
                  <span v-else class="text-n-slate-6 text-xs">—</span>
                </td>

                <td class="px-4 py-3">
                  <span
                    v-if="booking.metadata?.origin"
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-medium border"
                    :class="{
                      'bg-emerald-50 text-emerald-700 border-emerald-200':
                        booking.metadata.origin === 'WhatsApp',
                      'bg-pink-50 text-pink-700 border-pink-200':
                        booking.metadata.origin === 'Instagram',
                      'bg-amber-50 text-amber-700 border-amber-200':
                        booking.metadata.origin === 'Balcão',
                    }"
                    >{{ booking.metadata.origin }}</span>
                  <span v-else class="text-n-slate-6 text-xs">—</span>
                </td>

                <td class="px-4 py-3">
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border"
                    :class="statusConfig(booking.status).classes"
                  >
                    <span
                      class="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      :style="{
                        backgroundColor:
                          STATUS_COLOR[booking.status] || '#94a3b8',
                      }"
                    />
                    {{ statusConfig(booking.status).label }}
                  </span>
                </td>

                <td class="px-4 py-3" @click.stop>
                  <div class="flex items-center gap-1">
                    <select
                      :value="booking.status"
                      class="text-xs px-2 py-1 rounded-lg border border-n-weak bg-n-background text-n-slate-11 focus:outline-none"
                      @change="
                        e => updateBookingStatus(booking, e.target.value)
                      "
                    >
                      <option
                        v-for="(cfg, key) in STATUS_CONFIG"
                        :key="key"
                        :value="key"
                      >
                        {{ cfg.label }}
                      </option>
                    </select>

                    <!-- Send message button -->
                    <button
                      v-if="booking.metadata?.contact_id"
                      class="flex items-center gap-1 px-2 py-1 rounded-lg text-white hover:opacity-80 transition-all text-xs font-medium"
                      style="background-color: #10b981"
                      title="Enviar mensagem de confirmação"
                      @click="openMessageModal(booking)"
                    >
                      <span class="i-lucide-message-circle size-3.5" />
                      Mensagem
                    </button>
                    <span
                      v-else
                      class="flex items-center gap-1 px-2 py-1 rounded-lg bg-n-alpha-1 text-n-slate-6 cursor-not-allowed text-xs border border-n-weak"
                      title="Vincule um contato para enviar mensagem"
                    >
                      <span class="i-lucide-message-circle size-3.5" />
                      Mensagem
                    </span>

                    <!-- Contact link -->
                    <a
                      v-if="booking.metadata?.contact_id"
                      :href="contactUrl(booking.metadata.contact_id)"
                      target="_blank"
                      class="p-1.5 rounded-lg hover:bg-n-alpha-2 text-n-slate-9 hover:text-n-slate-12 transition-colors"
                      title="Abrir contato"
                      @click.stop
                    >
                      <span class="i-lucide-user size-3.5" />
                    </a>

                    <a
                      v-if="booking.meet_url"
                      :href="booking.meet_url"
                      target="_blank"
                      class="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-all"
                      title="Abrir link do Google Meet"
                      @click.stop
                    >
                      <span class="i-lucide-video size-3.5" />
                      Meet
                    </a>
                    <span
                      v-else-if="
                        selectedSchedule?.integration_settings
                          ?.ical_secret_url &&
                        selectedSchedule?.integration_settings
                          ?.google_meet_enabled
                      "
                      class="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs text-n-slate-6 bg-n-alpha-1 border border-n-weak"
                      title="Nenhum evento do Google Agenda encontrado para este horário"
                    >
                      <span class="i-lucide-video-off size-3.5" />
                      Sem Meet
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ─────────────────────────────────────────────────
           TAB: PROFISSIONAIS
      ───────────────────────────────────────────────── -->
      <div
        v-else-if="activeTab === 'resources'"
        class="flex h-full overflow-hidden"
      >
        <!-- Main resources list -->
        <div class="flex-1 flex flex-col overflow-hidden">
          <div
            class="flex items-center justify-between px-6 py-4 border-b border-n-weak flex-shrink-0"
          >
            <div>
              <h2 class="text-sm font-semibold text-n-slate-12">
                Profissionais
              </h2>
              <p class="text-xs text-n-slate-8 mt-0.5">
                Crie profissionais externos ou importe agentes do Chatwoot.
              </p>
            </div>
            <div class="flex items-center gap-2">
              <button
                class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2 rounded-lg border border-n-weak transition-colors"
                @click="
                  showAgentImport = !showAgentImport;
                  loadAgents();
                "
              >
                <span class="i-lucide-users size-3.5" />
                Importar agente
              </button>
              <button
                class="flex items-center gap-1.5 px-3 py-1.5 bg-n-brand text-white text-xs font-medium rounded-lg hover:opacity-90 transition-all"
                @click="openResourceForm()"
              >
                <span class="i-lucide-plus size-3.5" />
                Novo profissional
              </button>
            </div>
          </div>

          <div class="flex-1 overflow-y-auto">
            <!-- Resource form (inline) -->
            <div
              v-if="showResourceForm"
              class="m-6 p-5 bg-n-alpha-1 rounded-2xl border border-n-weak space-y-3"
            >
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{
                  editingResourceId
                    ? 'Editar profissional'
                    : 'Novo profissional'
                }}
              </h3>

              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label class="block text-xs font-medium text-n-slate-11 mb-1">Nome *</label>
                  <input
                    v-model="resourceForm.name"
                    type="text"
                    placeholder="Nome do profissional"
                    class="w-full px-3 py-2 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30"
                  />
                </div>
                <div>
                  <label class="block text-xs font-medium text-n-slate-11 mb-1">E-mail</label>
                  <input
                    v-model="resourceForm.email"
                    type="email"
                    placeholder="email@exemplo.com"
                    class="w-full px-3 py-2 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30"
                  />
                </div>
                <div>
                  <label class="block text-xs font-medium text-n-slate-11 mb-1">Telefone</label>
                  <input
                    v-model="resourceForm.phone"
                    type="tel"
                    placeholder="+55 11 99999-0000"
                    class="w-full px-3 py-2 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30"
                  />
                </div>
                <div>
                  <label class="block text-xs font-medium text-n-slate-11 mb-1">Cor</label>
                  <input
                    v-model="resourceForm.color"
                    type="color"
                    class="w-full h-9 rounded-xl border border-n-weak bg-n-background cursor-pointer"
                  />
                </div>
              </div>

              <div class="flex items-center gap-2 pt-2">
                <button
                  class="px-4 py-2 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2 rounded-xl transition-colors"
                  @click="showResourceForm = false"
                >
                  Cancelar
                </button>
                <button
                  :disabled="!resourceForm.name.trim() || isSavingResource"
                  class="flex items-center gap-2 px-4 py-2 bg-n-brand text-white text-sm font-medium rounded-xl hover:opacity-90 disabled:opacity-50 transition-all"
                  @click="saveResource"
                >
                  <span
                    v-if="isSavingResource"
                    class="i-lucide-loader-2 size-3.5 animate-spin"
                  />
                  {{ isSavingResource ? 'Salvando...' : 'Salvar' }}
                </button>
              </div>
            </div>

            <!-- Agent import panel -->
            <div
              v-if="showAgentImport"
              class="mx-6 mt-4 p-5 bg-blue-50 border border-blue-200 rounded-2xl"
            >
              <h3 class="text-sm font-semibold text-blue-900 mb-3">
                Importar agentes Chatwoot
              </h3>
              <div class="space-y-2 max-h-48 overflow-y-auto">
                <div
                  v-for="agent in availableAgents"
                  :key="agent.id"
                  class="flex items-center gap-3 px-3 py-2 bg-white border border-blue-100 rounded-xl"
                >
                  <img
                    v-if="agent.avatar_url"
                    :src="agent.avatar_url"
                    class="w-7 h-7 rounded-full object-cover"
                  />
                  <div
                    v-else
                    class="w-7 h-7 rounded-full bg-blue-200 flex items-center justify-center text-xs font-semibold text-blue-800"
                  >
                    {{ (agent.name || agent.available_name || '?')[0] }}
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="text-xs font-medium text-n-slate-12 truncate">
                      {{ agent.name || agent.available_name }}
                    </div>
                    <div class="text-xs text-n-slate-8 truncate">
                      {{ agent.email }}
                    </div>
                  </div>
                  <button
                    :disabled="isSavingResource"
                    class="text-xs px-2.5 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    @click="importAgent(agent)"
                  >
                    Importar
                  </button>
                </div>
                <p
                  v-if="availableAgents.length === 0"
                  class="text-xs text-blue-700 text-center py-2"
                >
                  {{
                    agents.length === 0
                      ? 'Nenhum agente disponível.'
                      : 'Todos os agentes já foram importados.'
                  }}
                </p>
              </div>
              <button
                class="mt-3 text-xs text-blue-700 hover:underline"
                @click="showAgentImport = false"
              >
                Fechar
              </button>
            </div>

            <!-- Resources grid -->
            <div
              class="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              <div
                v-for="resource in resources"
                :key="resource.id"
                class="bg-n-background border border-n-weak rounded-2xl p-4 hover:shadow-md transition-all"
                :class="!resource.active ? 'opacity-60' : ''"
              >
                <div class="flex items-start justify-between mb-3">
                  <div class="flex items-center gap-2.5">
                    <div
                      class="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
                      :style="{ backgroundColor: resource.color }"
                    >
                      {{ resource.name[0]?.toUpperCase() }}
                    </div>
                    <div>
                      <div class="text-sm font-semibold text-n-slate-12">
                        {{ resource.name }}
                      </div>
                      <div class="flex items-center gap-1 mt-0.5">
                        <span
                          class="text-xs px-1.5 py-0.5 rounded-full"
                          :class="
                            resource.source_type === 'platform_user'
                              ? 'bg-blue-50 text-blue-700'
                              : 'bg-n-alpha-1 text-n-slate-8'
                          "
                        >
                          {{
                            resource.source_type === 'platform_user'
                              ? 'Plataforma'
                              : 'Externo'
                          }}
                        </span>
                        <span
                          v-if="!resource.active"
                          class="text-xs px-1.5 py-0.5 rounded-full bg-n-alpha-1 text-n-slate-7"
                        >
                          Inativo
                        </span>
                      </div>
                    </div>
                  </div>

                  <div class="flex items-center gap-1">
                    <button
                      class="p-1.5 rounded-lg hover:bg-n-alpha-2 text-n-slate-8 transition-colors"
                      @click="openResourceForm(resource)"
                    >
                      <span class="i-lucide-pencil size-3.5" />
                    </button>
                    <button
                      class="p-1.5 rounded-lg hover:bg-red-50 text-n-slate-8 hover:text-red-600 transition-colors"
                      @click="deleteResource(resource)"
                    >
                      <span class="i-lucide-trash-2 size-3.5" />
                    </button>
                  </div>
                </div>

                <div class="space-y-1">
                  <div
                    v-if="resource.email"
                    class="flex items-center gap-1.5 text-xs text-n-slate-9"
                  >
                    <span class="i-lucide-mail size-3" />
                    <span class="truncate">{{ resource.email }}</span>
                  </div>
                  <div
                    v-if="resource.phone"
                    class="flex items-center gap-1.5 text-xs text-n-slate-9"
                  >
                    <span class="i-lucide-phone size-3" />
                    <span>{{ resource.phone }}</span>
                  </div>
                </div>
              </div>

              <div
                v-if="resources.length === 0"
                class="col-span-3 flex flex-col items-center justify-center py-12 gap-3 text-center"
              >
                <span class="i-lucide-users size-10 text-n-slate-6" />
                <p class="text-sm text-n-slate-9">
                  Nenhum profissional cadastrado ainda.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ─────────────────────────────────────────────────
           TAB: KPI / RELATÓRIOS
      ───────────────────────────────────────────────── -->
      <div
        v-else-if="activeTab === 'kpi'"
        class="flex flex-col h-full overflow-y-auto"
      >
        <!-- Sub-header -->
        <div class="flex-shrink-0 border-b border-n-weak">
          <div class="flex items-center justify-between px-6 py-3">
            <div>
              <h2 class="text-sm font-semibold text-n-slate-12">
                Relatórios & Performance
              </h2>
              <p class="text-xs text-n-slate-8 mt-0.5">
                {{ selectedSchedule?.title || 'Selecione uma agenda' }}
                · {{ statsBookings.length }} agendamentos ·
                {{ statsPeriodLabel }}
              </p>
            </div>
            <button
              class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-n-slate-9 hover:bg-n-alpha-2 rounded-lg border border-n-weak transition-colors"
              :class="{ 'opacity-50 pointer-events-none': isLoadingStats }"
              @click="reloadKpi"
            >
              <span
                class="i-lucide-refresh-cw size-3.5"
                :class="{ 'animate-spin': isLoadingStats }"
              />
              Atualizar
            </button>
          </div>

          <!-- Period filter pills -->
          <div class="flex flex-wrap items-center gap-1.5 px-6 pb-3">
            <button
              v-for="p in STAT_PERIODS"
              :key="p.key"
              class="px-3 py-1.5 text-xs font-medium rounded-lg transition-all"
              :class="
                statsPeriod === p.key
                  ? 'bg-n-brand text-white'
                  : 'text-n-slate-9 hover:bg-n-alpha-2 border border-transparent hover:border-n-weak'
              "
              @click="statsPeriod = p.key"
            >
              {{ p.label }}
            </button>

            <!-- Custom date range inputs — shown only when "Personalizado" is active -->
            <template v-if="statsPeriod === 'custom'">
              <span class="w-px h-5 bg-n-weak mx-1" />
              <div class="flex items-center gap-1.5">
                <label class="text-xs text-n-slate-8 flex-shrink-0">De</label>
                <input
                  v-model="customStartDate"
                  type="date"
                  class="px-2 py-1 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all"
                />
                <label class="text-xs text-n-slate-8 flex-shrink-0">até</label>
                <input
                  v-model="customEndDate"
                  type="date"
                  :min="customStartDate"
                  class="px-2 py-1 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all"
                />
              </div>
            </template>
          </div>
        </div>

        <!-- Loading -->
        <div
          v-if="isLoadingStats"
          class="flex-1 flex items-center justify-center"
        >
          <div class="text-center">
            <span
              class="i-lucide-loader-2 size-8 text-n-slate-6 animate-spin block mx-auto mb-3"
            />
            <p class="text-sm text-n-slate-8">Carregando dados...</p>
          </div>
        </div>

        <div v-else-if="stats" class="p-6 space-y-5">
          <!-- ── Row 1: 4 rate KPIs ── -->
          <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <!-- Total -->
            <div
              class="bg-n-background border border-n-weak rounded-2xl p-5 flex items-center gap-4"
            >
              <div
                class="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                style="background-color: #6366f118"
              >
                <span class="i-lucide-calendar size-6" style="color: #6366f1" />
              </div>
              <div>
                <div class="text-3xl font-bold text-n-slate-12">
                  {{ stats.total }}
                </div>
                <div class="text-xs text-n-slate-8 mt-0.5">
                  Total de agendamentos
                </div>
                <div class="text-xs font-medium mt-1" style="color: #3b82f6">
                  {{ stats.upcoming }} próximos
                </div>
              </div>
            </div>

            <!-- Attendance rate -->
            <div class="bg-n-background border border-n-weak rounded-2xl p-5">
              <div class="flex items-center justify-between mb-3">
                <span
                  class="text-xs font-semibold text-n-slate-9 uppercase tracking-wide"
                  >Taxa de Presença</span>
                <span
                  class="i-lucide-user-check size-4"
                  style="color: #10b981"
                />
              </div>
              <div class="text-3xl font-bold" style="color: #10b981">
                {{ attendanceRate }}%
              </div>
              <div class="mt-2 h-1.5 bg-n-alpha-2 rounded-full overflow-hidden">
                <div
                  class="h-full rounded-full transition-all duration-700"
                  :style="{
                    width: attendanceRate + '%',
                    backgroundColor: '#10b981',
                  }"
                />
              </div>
              <div class="text-xs text-n-slate-8 mt-1.5">
                {{ stats.completed }} realizados
              </div>
            </div>

            <!-- Cancellation rate -->
            <div class="bg-n-background border border-n-weak rounded-2xl p-5">
              <div class="flex items-center justify-between mb-3">
                <span
                  class="text-xs font-semibold text-n-slate-9 uppercase tracking-wide"
                  >Cancelamentos</span>
                <span class="i-lucide-x-circle size-4" style="color: #ef4444" />
              </div>
              <div class="text-3xl font-bold" style="color: #ef4444">
                {{ cancellationRate }}%
              </div>
              <div class="mt-2 h-1.5 bg-n-alpha-2 rounded-full overflow-hidden">
                <div
                  class="h-full rounded-full transition-all duration-700"
                  :style="{
                    width: cancellationRate + '%',
                    backgroundColor: '#ef4444',
                  }"
                />
              </div>
              <div class="text-xs text-n-slate-8 mt-1.5">
                {{ stats.cancelled }} cancelados
              </div>
            </div>

            <!-- No-show rate -->
            <div class="bg-n-background border border-n-weak rounded-2xl p-5">
              <div class="flex items-center justify-between mb-3">
                <span
                  class="text-xs font-semibold text-n-slate-9 uppercase tracking-wide"
                  >No-show</span>
                <span class="i-lucide-user-x size-4" style="color: #f97316" />
              </div>
              <div class="text-3xl font-bold" style="color: #f97316">
                {{ noShowRate }}%
              </div>
              <div class="mt-2 h-1.5 bg-n-alpha-2 rounded-full overflow-hidden">
                <div
                  class="h-full rounded-full transition-all duration-700"
                  :style="{
                    width: noShowRate + '%',
                    backgroundColor: '#f97316',
                  }"
                />
              </div>
              <div class="text-xs text-n-slate-8 mt-1.5">
                {{ stats.no_show }} ausências
              </div>
            </div>
          </div>

          <!-- ── Row 2: Status distribution + Upcoming ── -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <!-- Status distribution -->
            <div class="bg-n-background border border-n-weak rounded-2xl p-5">
              <div class="flex items-center gap-2 mb-4">
                <span class="i-lucide-pie-chart size-4 text-n-slate-9" />
                <h3 class="text-sm font-semibold text-n-slate-12">
                  Distribuição por Status
                </h3>
                <span class="ml-auto text-xs text-n-slate-8">{{ stats.total }} total</span>
              </div>

              <!-- Stacked bar -->
              <div class="flex h-3 rounded-full overflow-hidden mb-4 gap-0.5">
                <div
                  v-for="s in statusDistribution"
                  :key="s.key"
                  class="transition-all duration-700"
                  :style="{
                    width: s.pct + '%',
                    minWidth: s.value > 0 ? '4px' : '0',
                    backgroundColor: s.color,
                  }"
                />
              </div>

              <!-- Legend rows -->
              <div class="space-y-2.5">
                <div
                  v-for="s in statusDistribution"
                  :key="s.key"
                  class="flex items-center gap-3"
                >
                  <span
                    class="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    :style="{ backgroundColor: s.color }"
                  />
                  <span class="flex-1 text-xs text-n-slate-11">{{
                    s.label
                  }}</span>
                  <div class="flex items-center gap-3">
                    <div
                      class="w-32 h-1.5 bg-n-alpha-2 rounded-full overflow-hidden"
                    >
                      <div
                        class="h-full rounded-full"
                        :style="{
                          width: s.pct + '%',
                          backgroundColor: s.color,
                        }"
                      />
                    </div>
                    <span
                      class="text-xs font-semibold text-n-slate-12 w-6 text-right"
                      >{{ s.value }}</span>
                    <span class="text-xs text-n-slate-7 w-8 text-right">{{ s.pct }}%</span>
                  </div>
                </div>
              </div>

              <!-- Extra metrics row -->
              <div
                class="grid grid-cols-2 gap-3 mt-5 pt-4 border-t border-n-weak"
              >
                <div class="text-center">
                  <div class="text-xl font-bold text-n-slate-12">
                    {{ confirmationRate }}%
                  </div>
                  <div class="text-xs text-n-slate-8 mt-0.5">
                    Taxa de confirmação
                  </div>
                </div>
                <div class="text-center">
                  <div class="text-xl font-bold text-n-slate-12">
                    {{ avgSlotQty }}
                  </div>
                  <div class="text-xs text-n-slate-8 mt-0.5">
                    Vagas médias/agend.
                  </div>
                </div>
              </div>
            </div>

            <!-- Upcoming bookings -->
            <div
              class="bg-n-background border border-n-weak rounded-2xl p-5 flex flex-col"
            >
              <div class="flex items-center gap-2 mb-4">
                <span class="i-lucide-calendar-clock size-4 text-n-slate-9" />
                <h3 class="text-sm font-semibold text-n-slate-12">
                  Próximos Agendamentos
                </h3>
                <span
                  class="ml-auto text-xs font-medium px-2 py-0.5 rounded-full border"
                  style="
                    background-color: #3b82f618;
                    color: #3b82f6;
                    border-color: #3b82f630;
                  "
                >
                  {{ stats.upcoming }} pendentes
                </span>
              </div>

              <div
                v-if="upcomingBookings.length === 0"
                class="flex-1 flex flex-col items-center justify-center gap-2 text-center py-6"
              >
                <span class="i-lucide-calendar-check size-8 text-n-slate-5" />
                <p class="text-xs text-n-slate-8">Nenhum agendamento futuro.</p>
              </div>

              <div v-else class="space-y-2 overflow-y-auto max-h-72">
                <div
                  v-for="b in upcomingBookings"
                  :key="b.id"
                  class="flex items-center gap-3 p-2.5 rounded-xl border border-n-weak hover:bg-n-alpha-1 transition-colors cursor-pointer"
                  @click="
                    activeTab = 'bookings';
                    openEditBooking(b);
                  "
                >
                  <div
                    class="w-9 h-9 rounded-xl flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                    :style="{ backgroundColor: b.resource?.color || '#6366f1' }"
                  >
                    {{ (b.attendee_name || '?')[0].toUpperCase() }}
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="text-xs font-semibold text-n-slate-12 truncate">
                      {{ b.attendee_name }}
                    </div>
                    <div class="text-xs text-n-slate-8 truncate">
                      {{ format(parseISO(b.starts_at), 'dd/MM HH:mm') }}
                      <span v-if="b.resource"> · {{ b.resource.name }}</span>
                    </div>
                  </div>
                  <span
                    class="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium border"
                    :class="statusConfig(b.status).classes"
                  >
                    <span
                      class="w-1.5 h-1.5 rounded-full"
                      :style="{ backgroundColor: STATUS_COLOR[b.status] }"
                    />
                    {{ statusConfig(b.status).label }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- ── Row 3: DOW + Hourly distribution ── -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <!-- By day of week -->
            <div class="bg-n-background border border-n-weak rounded-2xl p-5">
              <div class="flex items-center gap-2 mb-4">
                <span class="i-lucide-calendar-days size-4 text-n-slate-9" />
                <h3 class="text-sm font-semibold text-n-slate-12">
                  Agendamentos por Dia da Semana
                </h3>
              </div>

              <div
                v-if="statsBookings.length === 0"
                class="text-xs text-n-slate-8 text-center py-6"
              >
                Sem dados para o período atual.
              </div>

              <div v-else class="space-y-2.5">
                <div
                  v-for="d in bookingsByDow"
                  :key="d.day"
                  class="flex items-center gap-3"
                >
                  <span
                    class="text-xs font-semibold text-n-slate-9 w-7 flex-shrink-0"
                    >{{ d.day }}</span>
                  <div
                    class="flex-1 h-5 bg-n-alpha-1 rounded-lg overflow-hidden"
                  >
                    <div
                      class="h-full rounded-lg transition-all duration-500 flex items-center justify-end pr-2"
                      :style="{
                        width: d.pct + '%',
                        minWidth: d.count > 0 ? '28px' : '0',
                        backgroundColor: selectedSchedule?.color || '#6366f1',
                        opacity: d.pct > 0 ? 0.7 + d.pct / 300 : 0.15,
                      }"
                    >
                      <span
                        v-if="d.count > 0"
                        class="text-xs font-bold text-white leading-none"
                        >{{ d.count }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- By hour -->
            <div class="bg-n-background border border-n-weak rounded-2xl p-5">
              <div class="flex items-center gap-2 mb-4">
                <span class="i-lucide-clock size-4 text-n-slate-9" />
                <h3 class="text-sm font-semibold text-n-slate-12">
                  Distribuição por Horário
                </h3>
              </div>

              <div
                v-if="bookingsByHour.length === 0"
                class="text-xs text-n-slate-8 text-center py-6"
              >
                Sem dados para o período atual.
              </div>

              <div v-else class="space-y-2 max-h-56 overflow-y-auto">
                <div
                  v-for="h in bookingsByHour"
                  :key="h.hour"
                  class="flex items-center gap-3"
                >
                  <span
                    class="text-xs font-mono text-n-slate-9 w-12 flex-shrink-0"
                    >{{ h.hour }}</span>
                  <div
                    class="flex-1 h-5 bg-n-alpha-1 rounded-lg overflow-hidden"
                  >
                    <div
                      class="h-full rounded-lg transition-all duration-500 flex items-center justify-end pr-2"
                      :style="{
                        width: h.pct + '%',
                        minWidth: '28px',
                        backgroundColor: '#6366f1',
                        opacity: 0.6 + h.pct / 250,
                      }"
                    >
                      <span class="text-xs font-bold text-white leading-none">{{
                        h.count
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ── Row 4: Performance por profissional ── -->
          <div class="bg-n-background border border-n-weak rounded-2xl p-5">
            <div class="flex items-center gap-2 mb-5">
              <span class="i-lucide-users size-4 text-n-slate-9" />
              <h3 class="text-sm font-semibold text-n-slate-12">
                Performance por Profissional
              </h3>
            </div>

            <div
              v-if="!stats.by_resource?.length"
              class="text-xs text-n-slate-8 text-center py-4"
            >
              Nenhum profissional cadastrado.
            </div>

            <div v-else class="space-y-4">
              <div
                v-for="res in stats.by_resource"
                :key="res.id"
                class="flex items-center gap-4 p-3 rounded-xl border border-n-weak hover:bg-n-alpha-1 transition-colors"
              >
                <!-- Avatar -->
                <div
                  class="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
                  :style="{ backgroundColor: res.color || '#6366f1' }"
                >
                  {{ res.name[0] }}
                </div>

                <!-- Name + bar -->
                <div class="flex-1 min-w-0">
                  <div class="flex items-center justify-between mb-1.5">
                    <span
                      class="text-xs font-semibold text-n-slate-12 truncate"
                      >{{ res.name }}</span>
                    <span class="text-xs text-n-slate-8 flex-shrink-0 ml-2">
                      {{ res.total }} agendamentos
                    </span>
                  </div>
                  <div class="h-2 bg-n-alpha-2 rounded-full overflow-hidden">
                    <div
                      class="h-full rounded-full transition-all duration-500"
                      :style="{
                        width:
                          res.total > 0
                            ? `${(res.completed / res.total) * 100}%`
                            : '0%',
                        backgroundColor: res.color || '#6366f1',
                      }"
                    />
                  </div>
                </div>

                <!-- Metrics chips -->
                <div
                  class="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end max-w-48"
                >
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                    style="background-color: #10b98118; color: #10b981"
                  >
                    <span class="i-lucide-check-circle size-3" />
                    {{ res.confirmed }} conf.
                  </span>
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                    style="background-color: #3b82f618; color: #3b82f6"
                  >
                    <span class="i-lucide-check-check size-3" />
                    {{ res.completed }} realizados
                  </span>
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                    style="background-color: #6366f118; color: #6366f1"
                  >
                    <span class="i-lucide-calendar-clock size-3" />
                    {{ res.upcoming }} próximos
                  </span>
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                    style="background-color: #94a3b818; color: #94a3b8"
                  >
                    <span class="i-lucide-user-x size-3" />
                    {{ res.no_show }} no-show
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- ── Row 5: Performance por Tipo de Agendamento ── -->
          <div class="bg-n-background border border-n-weak rounded-2xl p-5">
            <div class="flex items-center gap-2 mb-5">
              <span class="i-lucide-tag size-4 text-n-slate-9" />
              <h3 class="text-sm font-semibold text-n-slate-12">
                Performance por Tipo de Agendamento
              </h3>
            </div>

            <div
              v-if="!bookingsByType.length"
              class="text-xs text-n-slate-8 text-center py-4"
            >
              Nenhum dado disponível.
            </div>

            <div v-else class="space-y-4">
              <div
                v-for="(item, idx) in bookingsByType"
                :key="item.label"
                class="flex items-center gap-4 p-3 rounded-xl border border-n-weak hover:bg-n-alpha-1 transition-colors"
              >
                <div
                  class="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
                  :style="{
                    backgroundColor: [
                      '#6366f1',
                      '#8b5cf6',
                      '#a855f7',
                      '#d946ef',
                      '#ec4899',
                    ][idx % 5],
                  }"
                >
                  {{ item.label[0]?.toUpperCase() }}
                </div>

                <div class="flex-1 min-w-0">
                  <div class="flex items-center justify-between mb-1.5">
                    <span
                      class="text-xs font-semibold text-n-slate-12 truncate"
                      >{{ item.label }}</span>
                    <span class="text-xs text-n-slate-8 flex-shrink-0 ml-2">{{ item.total }} agendamentos</span>
                  </div>
                  <div class="h-2 bg-n-alpha-2 rounded-full overflow-hidden">
                    <div
                      class="h-full rounded-full transition-all duration-500"
                      :style="{
                        width:
                          item.total > 0
                            ? `${(item.completed / item.total) * 100}%`
                            : '0%',
                        backgroundColor: [
                          '#6366f1',
                          '#8b5cf6',
                          '#a855f7',
                          '#d946ef',
                          '#ec4899',
                        ][idx % 5],
                      }"
                    />
                  </div>
                </div>

                <div
                  class="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end max-w-48"
                >
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                    style="background-color: #10b98118; color: #10b981"
                  >
                    <span class="i-lucide-check-circle size-3" />
                    {{ item.confirmed }} conf.
                  </span>
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                    style="background-color: #3b82f618; color: #3b82f6"
                  >
                    <span class="i-lucide-check-check size-3" />
                    {{ item.completed }} realizados
                  </span>
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                    style="background-color: #6366f118; color: #6366f1"
                  >
                    <span class="i-lucide-calendar-clock size-3" />
                    {{ item.upcoming }} próximos
                  </span>
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                    style="background-color: #94a3b818; color: #94a3b8"
                  >
                    <span class="i-lucide-user-x size-3" />
                    {{ item.no_show }} no-show
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- ── Row 6: Performance por Tipo de Produto ── -->
          <div class="bg-n-background border border-n-weak rounded-2xl p-5">
            <div class="flex items-center gap-2 mb-5">
              <span class="i-lucide-package size-4 text-n-slate-9" />
              <h3 class="text-sm font-semibold text-n-slate-12">
                Performance por Tipo de Produto
              </h3>
            </div>

            <div
              v-if="!bookingsByProduct.length"
              class="text-xs text-n-slate-8 text-center py-4"
            >
              Nenhum dado disponível.
            </div>

            <div v-else class="space-y-4">
              <div
                v-for="(item, idx) in bookingsByProduct"
                :key="item.label"
                class="flex items-center gap-4 p-3 rounded-xl border border-n-weak hover:bg-n-alpha-1 transition-colors"
              >
                <div
                  class="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
                  :style="{
                    backgroundColor: [
                      '#f59e0b',
                      '#f97316',
                      '#ef4444',
                      '#14b8a6',
                      '#06b6d4',
                    ][idx % 5],
                  }"
                >
                  {{ item.label[0]?.toUpperCase() }}
                </div>

                <div class="flex-1 min-w-0">
                  <div class="flex items-center justify-between mb-1.5">
                    <span
                      class="text-xs font-semibold text-n-slate-12 truncate"
                      >{{ item.label }}</span>
                    <span class="text-xs text-n-slate-8 flex-shrink-0 ml-2">{{ item.total }} agendamentos</span>
                  </div>
                  <div class="h-2 bg-n-alpha-2 rounded-full overflow-hidden">
                    <div
                      class="h-full rounded-full transition-all duration-500"
                      :style="{
                        width:
                          item.total > 0
                            ? `${(item.completed / item.total) * 100}%`
                            : '0%',
                        backgroundColor: [
                          '#f59e0b',
                          '#f97316',
                          '#ef4444',
                          '#14b8a6',
                          '#06b6d4',
                        ][idx % 5],
                      }"
                    />
                  </div>
                </div>

                <div
                  class="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end max-w-48"
                >
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                    style="background-color: #10b98118; color: #10b981"
                  >
                    <span class="i-lucide-check-circle size-3" />
                    {{ item.confirmed }} conf.
                  </span>
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                    style="background-color: #3b82f618; color: #3b82f6"
                  >
                    <span class="i-lucide-check-check size-3" />
                    {{ item.completed }} realizados
                  </span>
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                    style="background-color: #f59e0b18; color: #f59e0b"
                  >
                    <span class="i-lucide-calendar-clock size-3" />
                    {{ item.upcoming }} próximos
                  </span>
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                    style="background-color: #94a3b818; color: #94a3b8"
                  >
                    <span class="i-lucide-user-x size-3" />
                    {{ item.no_show }} no-show
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ─────────────────────────────────────────────────
           TAB: CONFIGURAÇÕES
      ───────────────────────────────────────────────── -->
      <div
        v-else-if="activeTab === 'settings'"
        class="flex flex-col h-full overflow-y-auto p-6"
      >
        <div
          v-if="!selectedSchedule"
          class="flex-1 flex flex-col items-center justify-center gap-3 text-center"
        >
          <span class="i-lucide-settings-2 size-10 text-n-slate-6" />
          <p class="text-sm text-n-slate-9">
            Selecione uma agenda para editar as configurações.
          </p>
        </div>

        <div v-else class="max-w-2xl space-y-5">
          <!-- Header -->
          <div class="flex items-center justify-between">
            <div>
              <h2 class="text-sm font-semibold text-n-slate-12">
                Configurações — {{ selectedSchedule.title }}
              </h2>
              <p class="text-xs text-n-slate-8 mt-0.5">
                Integrações, webhooks e Google Agenda.
              </p>
            </div>
            <button
              class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2 rounded-lg border border-n-weak transition-colors"
              @click="openEditSchedule"
            >
              <span class="i-lucide-pencil size-3.5" />
              Editar disponibilidade
            </button>
          </div>

          <!-- ── PRODUTOS ── -->
          <div
            class="bg-n-background border border-n-weak rounded-2xl p-5 space-y-4"
          >
            <div class="flex items-center gap-2">
              <span class="i-lucide-package size-4 text-n-brand" />
              <h3 class="text-sm font-semibold text-n-slate-12">
                Produtos / Serviços
              </h3>
            </div>
            <p class="text-xs text-n-slate-8">
              Liste os produtos ou serviços disponíveis nesta agenda. Eles podem
              ser selecionados ao criar um agendamento.
            </p>

            <div class="flex gap-2">
              <input
                v-model="newSettingsProduct"
                type="text"
                placeholder="Nome do produto ou serviço..."
                class="flex-1 px-3 py-2 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 placeholder-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all"
                @keydown.enter.prevent="addSettingsProduct"
              />
              <button
                type="button"
                class="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-n-brand text-white rounded-xl hover:opacity-90 transition-all"
                @click="addSettingsProduct"
              >
                <span class="i-lucide-plus size-3.5" />
                Adicionar
              </button>
            </div>

            <div
              v-if="settingsForm.integration_settings.products.length > 0"
              class="flex flex-wrap gap-2"
            >
              <span
                v-for="(product, i) in settingsForm.integration_settings
                  .products"
                :key="i"
                class="flex items-center gap-1.5 px-3 py-1.5 bg-n-alpha-2 border border-n-weak rounded-xl text-xs text-n-slate-11"
              >
                {{ product }}
                <button
                  type="button"
                  class="text-n-slate-8 hover:text-red-500 transition-colors"
                  @click="removeSettingsProduct(i)"
                >
                  <span class="i-lucide-x size-3" />
                </button>
              </span>
            </div>

            <p v-else class="text-xs text-n-slate-7 italic">
              Nenhum produto cadastrado. Adicione acima.
            </p>
          </div>

          <!-- ── TIPOS DE AGENDAMENTO ── -->
          <div
            class="bg-n-background border border-n-weak rounded-2xl p-5 space-y-4"
          >
            <div class="flex items-center gap-2">
              <span class="i-lucide-tag size-4 text-n-brand" />
              <h3 class="text-sm font-semibold text-n-slate-12">
                Tipos de Agendamento
              </h3>
            </div>
            <p class="text-xs text-n-slate-8">
              Liste os tipos de agendamento disponíveis nesta agenda. Eles podem
              ser selecionados ao criar um agendamento.
            </p>

            <div class="flex gap-2">
              <input
                v-model="newSettingsType"
                type="text"
                placeholder="Ex: Consulta, Retorno, Avaliação..."
                class="flex-1 px-3 py-2 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 placeholder-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all"
                @keydown.enter.prevent="addSettingsType"
              />
              <button
                type="button"
                class="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-n-brand text-white rounded-xl hover:opacity-90 transition-all"
                @click="addSettingsType"
              >
                <span class="i-lucide-plus size-3.5" />
                Adicionar
              </button>
            </div>

            <div
              v-if="
                settingsForm.integration_settings.appointment_types.length > 0
              "
              class="flex flex-wrap gap-2"
            >
              <span
                v-for="(type, i) in settingsForm.integration_settings
                  .appointment_types"
                :key="i"
                class="flex items-center gap-1.5 px-3 py-1.5 bg-n-alpha-2 border border-n-weak rounded-xl text-xs text-n-slate-11"
              >
                {{ type }}
                <button
                  type="button"
                  class="text-n-slate-8 hover:text-red-500 transition-colors"
                  @click="removeSettingsType(i)"
                >
                  <span class="i-lucide-x size-3" />
                </button>
              </span>
            </div>

            <p v-else class="text-xs text-n-slate-7 italic">
              Nenhum tipo cadastrado. Adicione acima.
            </p>
          </div>

          <!-- ── ORIGENS DO AGENDAMENTO ── -->
          <div
            class="bg-n-background border border-n-weak rounded-2xl p-5 space-y-4"
          >
            <div class="flex items-center gap-2">
              <span class="i-lucide-map-pin size-4 text-n-brand" />
              <h3 class="text-sm font-semibold text-n-slate-12">
                Origens do Agendamento
              </h3>
            </div>
            <p class="text-xs text-n-slate-8">
              Liste os canais ou origens disponíveis ao criar um agendamento
              (ex: WhatsApp, Instagram, Balcão). Os dados de origem já gravados
              nos agendamentos não são afetados.
            </p>

            <div class="flex gap-2">
              <input
                v-model="newSettingsOrigin"
                type="text"
                placeholder="Ex: WhatsApp, Telefone, Site..."
                class="flex-1 px-3 py-2 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 placeholder-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all"
                @keydown.enter.prevent="addSettingsOrigin"
              />
              <button
                type="button"
                class="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-n-brand text-white rounded-xl hover:opacity-90 transition-all"
                @click="addSettingsOrigin"
              >
                <span class="i-lucide-plus size-3.5" />
                Adicionar
              </button>
            </div>

            <div
              v-if="
                settingsForm.integration_settings.booking_origins.length > 0
              "
              class="flex flex-wrap gap-2"
            >
              <span
                v-for="(origin, i) in settingsForm.integration_settings
                  .booking_origins"
                :key="i"
                class="flex items-center gap-1.5 px-3 py-1.5 bg-n-alpha-2 border border-n-weak rounded-xl text-xs text-n-slate-11"
              >
                {{ origin }}
                <button
                  type="button"
                  class="text-n-slate-8 hover:text-red-500 transition-colors"
                  @click="removeSettingsOrigin(i)"
                >
                  <span class="i-lucide-x size-3" />
                </button>
              </span>
            </div>

            <p v-else class="text-xs text-n-slate-7 italic">
              Nenhuma origem cadastrada. Adicione acima.
            </p>
          </div>

          <!-- ── RECEBER: N8N → SIC ── -->
          <div
            class="bg-n-background border border-n-weak rounded-2xl p-5 space-y-4"
          >
            <div class="flex items-center gap-2">
              <span
                class="i-lucide-arrow-down-to-line size-4"
                style="color: #10b981"
              />
              <h3 class="text-sm font-semibold text-n-slate-12">
                Receber requisições (N8N → SIC)
              </h3>
              <span
                class="ml-auto text-xs px-2 py-0.5 rounded-full font-medium bg-emerald-50 text-emerald-700 border border-emerald-200"
                >Ativo</span>
            </div>

            <p class="text-xs text-n-slate-8">
              Use esta URL no nó <strong>HTTP Request</strong> do N8N para criar
              agendamentos, consultar disponibilidade e gerenciar bookings
              diretamente no SIC — sem autenticação adicional.
            </p>

            <!-- URL box -->
            <div>
              <label class="block text-xs font-semibold text-n-slate-11 mb-1">URL do Webhook de Entrada</label>
              <div class="flex items-center gap-2">
                <code
                  class="flex-1 text-xs font-mono bg-n-alpha-1 border border-n-weak rounded-xl px-3 py-2.5 text-n-slate-12 break-all select-all"
                >
                  {{ incomingWebhookUrl || 'Salve a agenda para gerar a URL' }}
                </code>
                <button
                  v-if="incomingWebhookUrl"
                  class="flex-shrink-0 p-2 rounded-xl border border-n-weak hover:bg-n-alpha-2 text-n-slate-9 transition-colors"
                  title="Copiar URL"
                  @click="copyToClipboard(incomingWebhookUrl)"
                >
                  <span class="i-lucide-copy size-3.5" />
                </button>
                <button
                  class="flex-shrink-0 flex items-center gap-1 px-2.5 py-2 rounded-xl border border-n-weak hover:bg-n-alpha-2 text-n-slate-9 text-xs transition-colors"
                  :class="{
                    'opacity-50 pointer-events-none': isRegeneratingSecret,
                  }"
                  title="Regenerar secret (invalida URL atual)"
                  @click="regenerateWebhookSecret"
                >
                  <span
                    class="i-lucide-refresh-cw size-3.5"
                    :class="{ 'animate-spin': isRegeneratingSecret }"
                  />
                  Regenerar
                </button>
              </div>
              <p class="text-xs text-n-slate-7 mt-1">
                O secret é parte da URL e funciona como autenticação. Clique em
                "Regenerar" para invalidar a URL atual e criar uma nova.
              </p>
            </div>

            <!-- Actions reference -->
            <div class="space-y-3">
              <p class="text-xs font-semibold text-n-slate-10">
                Ações disponíveis (campo
                <code class="font-mono bg-n-alpha-2 px-1 rounded">action_type</code>):
              </p>

              <!-- create_booking -->
              <div class="rounded-xl border border-n-weak overflow-hidden">
                <div
                  class="flex items-center gap-2 px-3 py-2 bg-emerald-50 border-b border-emerald-100"
                >
                  <span class="text-xs font-bold text-emerald-700 font-mono">create_booking</span>
                  <span class="ml-auto text-xs text-emerald-600">Cria um novo agendamento</span>
                </div>
                <pre
                  class="text-xs text-n-slate-9 font-mono leading-relaxed p-3 overflow-x-auto bg-n-alpha-1"
                  >{{
                    JSON.stringify(
                      {
                        action_type: 'create_booking',
                        starts_at: '2026-03-15T10:00:00-03:00',
                        ends_at: '2026-03-15T10:30:00-03:00',
                        attendee_name: 'João Silva',
                        attendee_email: 'joao@email.com',
                        attendee_phone: '+5511999999999',
                        slot_quantity: 1,
                        status: 'confirmed',
                        notes: 'Observação opcional',
                        appointment_resource_id: null,
                        metadata: { contact_id: 123 },
                      },
                      null,
                      2
                    )
                  }}</pre>
              </div>

              <!-- update_booking -->
              <div class="rounded-xl border border-n-weak overflow-hidden">
                <div
                  class="flex items-center gap-2 px-3 py-2 bg-blue-50 border-b border-blue-100"
                >
                  <span class="text-xs font-bold text-blue-700 font-mono">update_booking</span>
                  <span class="ml-auto text-xs text-blue-600">Atualiza status ou dados</span>
                </div>
                <pre
                  class="text-xs text-n-slate-9 font-mono leading-relaxed p-3 overflow-x-auto bg-n-alpha-1"
                  >{{
                    JSON.stringify(
                      {
                        action_type: 'update_booking',
                        booking_id: 42,
                        status: 'confirmed',
                        notes: 'Observação atualizada',
                        meet_url: 'https://meet.google.com/xyz',
                      },
                      null,
                      2
                    )
                  }}</pre>
              </div>

              <!-- cancel_booking -->
              <div class="rounded-xl border border-n-weak overflow-hidden">
                <div
                  class="flex items-center gap-2 px-3 py-2 bg-red-50 border-b border-red-100"
                >
                  <span class="text-xs font-bold text-red-700 font-mono">cancel_booking</span>
                  <span class="ml-auto text-xs text-red-600">Cancela um agendamento</span>
                </div>
                <pre
                  class="text-xs text-n-slate-9 font-mono leading-relaxed p-3 overflow-x-auto bg-n-alpha-1"
                  >{{
                    JSON.stringify(
                      {
                        action_type: 'cancel_booking',
                        booking_id: 42,
                      },
                      null,
                      2
                    )
                  }}</pre>
              </div>

              <!-- divider -->
              <div class="flex items-center gap-2 pt-1">
                <div class="flex-1 h-px bg-n-weak" />
                <span
                  class="text-xs text-n-slate-8 font-semibold uppercase tracking-wide"
                  >Consultas de disponibilidade</span>
                <div class="flex-1 h-px bg-n-weak" />
              </div>

              <!-- next_slots -->
              <div class="rounded-xl border border-n-weak overflow-hidden">
                <div
                  class="flex items-center gap-2 px-3 py-2 bg-violet-50 border-b border-violet-100"
                >
                  <span class="text-xs font-bold text-violet-700 font-mono">next_slots</span>
                  <span class="ml-auto text-xs text-violet-600">Próximos N horários disponíveis imediatos</span>
                </div>
                <pre
                  class="text-xs text-n-slate-9 font-mono leading-relaxed p-3 overflow-x-auto bg-n-alpha-1"
                  >{{
                    JSON.stringify(
                      {
                        action_type: 'next_slots',
                        count: 5,
                        from: 'agora (opcional, padrão: now)',
                        days_ahead: 60,
                      },
                      null,
                      2
                    )
                  }}</pre>
                <div class="px-3 pb-3 bg-n-alpha-1">
                  <p class="text-xs text-n-slate-7 font-semibold mb-1">
                    Resposta:
                  </p>
                  <pre
                    class="text-xs text-n-slate-9 font-mono leading-relaxed overflow-x-auto"
                    >{{
                      JSON.stringify(
                        {
                          action_type: 'next_slots',
                          count: 3,
                          timezone: 'America/Sao_Paulo',
                          slots: [
                            {
                              date: '2026-03-18',
                              weekday: 'Quarta',
                              time: '10:00',
                              starts_at: '2026-03-18T10:00:00-03:00',
                              ends_at: '2026-03-18T10:30:00-03:00',
                              remaining_slots: 2,
                            },
                            {
                              date: '2026-03-18',
                              weekday: 'Quarta',
                              time: '11:00',
                              starts_at: '2026-03-18T11:00:00-03:00',
                              ends_at: '2026-03-18T11:30:00-03:00',
                              remaining_slots: 1,
                            },
                            {
                              date: '2026-03-19',
                              weekday: 'Quinta',
                              time: '09:00',
                              starts_at: '2026-03-19T09:00:00-03:00',
                              ends_at: '2026-03-19T09:30:00-03:00',
                              remaining_slots: 2,
                            },
                          ],
                        },
                        null,
                        2
                      )
                    }}</pre>
                </div>
              </div>

              <!-- slots_for_day -->
              <div class="rounded-xl border border-n-weak overflow-hidden">
                <div
                  class="flex items-center gap-2 px-3 py-2 bg-amber-50 border-b border-amber-100"
                >
                  <span class="text-xs font-bold text-amber-700 font-mono">slots_for_day</span>
                  <span class="ml-auto text-xs text-amber-600">Horários livres em um dia específico</span>
                </div>
                <pre
                  class="text-xs text-n-slate-9 font-mono leading-relaxed p-3 overflow-x-auto bg-n-alpha-1"
                  >{{
                    JSON.stringify(
                      {
                        action_type: 'slots_for_day',
                        date: '2026-03-20',
                      },
                      null,
                      2
                    )
                  }}</pre>
                <div class="px-3 pb-3 bg-n-alpha-1">
                  <p class="text-xs text-n-slate-7 font-semibold mb-1">
                    Resposta:
                  </p>
                  <pre
                    class="text-xs text-n-slate-9 font-mono leading-relaxed overflow-x-auto"
                    >{{
                      JSON.stringify(
                        {
                          action_type: 'slots_for_day',
                          date: '2026-03-20',
                          weekday: 'Quinta',
                          count: 3,
                          slots: [
                            {
                              time: '09:00',
                              date: '2026-03-20',
                              weekday: 'Quinta',
                              starts_at: '2026-03-20T09:00:00-03:00',
                              ends_at: '2026-03-20T09:30:00-03:00',
                              remaining_slots: 2,
                            },
                            {
                              time: '10:00',
                              date: '2026-03-20',
                              weekday: 'Quinta',
                              starts_at: '2026-03-20T10:00:00-03:00',
                              ends_at: '2026-03-20T10:30:00-03:00',
                              remaining_slots: 2,
                            },
                            {
                              time: '11:00',
                              date: '2026-03-20',
                              weekday: 'Quinta',
                              starts_at: '2026-03-20T11:00:00-03:00',
                              ends_at: '2026-03-20T11:30:00-03:00',
                              remaining_slots: 1,
                            },
                          ],
                        },
                        null,
                        2
                      )
                    }}</pre>
                </div>
              </div>

              <!-- days_with_slot -->
              <div class="rounded-xl border border-n-weak overflow-hidden">
                <div
                  class="flex items-center gap-2 px-3 py-2 bg-cyan-50 border-b border-cyan-100"
                >
                  <span class="text-xs font-bold text-cyan-700 font-mono">days_with_slot</span>
                  <span class="ml-auto text-xs text-cyan-600">Dias que têm determinado horário livre</span>
                </div>
                <pre
                  class="text-xs text-n-slate-9 font-mono leading-relaxed p-3 overflow-x-auto bg-n-alpha-1"
                  >{{
                    JSON.stringify(
                      {
                        action_type: 'days_with_slot',
                        time: '10:00',
                        days_ahead: 30,
                        count: 5,
                      },
                      null,
                      2
                    )
                  }}</pre>
                <div class="px-3 pb-3 bg-n-alpha-1">
                  <p class="text-xs text-n-slate-7 font-semibold mb-1">
                    Resposta:
                  </p>
                  <pre
                    class="text-xs text-n-slate-9 font-mono leading-relaxed overflow-x-auto"
                    >{{
                      JSON.stringify(
                        {
                          action_type: 'days_with_slot',
                          time: '10:00',
                          count: 3,
                          days: [
                            {
                              date: '2026-03-17',
                              weekday: 'Terça',
                              starts_at: '2026-03-17T10:00:00-03:00',
                              remaining_slots: 2,
                            },
                            {
                              date: '2026-03-19',
                              weekday: 'Quinta',
                              starts_at: '2026-03-19T10:00:00-03:00',
                              remaining_slots: 1,
                            },
                            {
                              date: '2026-03-24',
                              weekday: 'Terça',
                              starts_at: '2026-03-24T10:00:00-03:00',
                              remaining_slots: 2,
                            },
                          ],
                        },
                        null,
                        2
                      )
                    }}</pre>
                </div>
              </div>
            </div>
          </div>

          <!-- ── ENVIAR: SIC → N8N ── -->
          <div
            class="bg-n-background border border-n-weak rounded-2xl p-5 space-y-4"
          >
            <div class="flex items-center gap-2">
              <span
                class="i-lucide-arrow-up-from-line size-4"
                style="color: #6366f1"
              />
              <h3 class="text-sm font-semibold text-n-slate-12">
                Enviar eventos (SIC → N8N)
              </h3>
            </div>
            <p class="text-xs text-n-slate-8">
              Configure as URLs dos webhooks no N8N. O SIC envia um
              <strong>HTTP POST</strong> automaticamente quando os eventos
              ocorrem.
            </p>

            <div>
              <label class="block text-xs font-semibold text-n-slate-11 mb-1">
                Webhook — Novo agendamento criado
              </label>
              <input
                v-model="settingsForm.integration_settings.booking_webhook_url"
                type="url"
                placeholder="https://seu-n8n.com/webhook/novo-agendamento"
                class="w-full px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 placeholder-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand font-mono transition-all"
              />
              <p class="text-xs text-n-slate-8 mt-1">
                Disparado ao criar um agendamento. Inclui dados do participante,
                contato Chatwoot, profissional e URL do Google Meet.
              </p>
            </div>

            <div>
              <label class="block text-xs font-semibold text-n-slate-11 mb-1">
                Webhook — Disponibilidade (override)
              </label>
              <input
                v-model="
                  settingsForm.integration_settings.availability_webhook_url
                "
                type="url"
                placeholder="https://seu-n8n.com/webhook/disponibilidade"
                class="w-full px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 placeholder-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand font-mono transition-all"
              />
              <p class="text-xs text-n-slate-8 mt-1">
                Recebe a grade calculada e pode retornar um array
                <code class="font-mono bg-n-alpha-2 px-1 rounded">slots</code>
                para sobrescrever os horários disponíveis.
              </p>
            </div>

            <!-- Payload example -->
            <div class="rounded-xl border border-n-weak overflow-hidden">
              <div
                class="flex items-center gap-2 px-3 py-2 bg-n-alpha-1 border-b border-n-weak"
              >
                <span class="text-xs font-semibold text-n-slate-10">Payload enviado pelo SIC no evento de agendamento:</span>
              </div>
              <pre
                class="text-xs text-n-slate-9 font-mono leading-relaxed p-3 overflow-x-auto bg-n-alpha-1"
                >{{
                  JSON.stringify(
                    {
                      id: 42,
                      status: 'confirmed',
                      starts_at: '2026-03-15T10:00:00-03:00',
                      ends_at: '2026-03-15T10:30:00-03:00',
                      attendee: {
                        name: 'João Silva',
                        email: 'joao@email.com',
                        phone: '+5511999999999',
                        contact_id: 123,
                      },
                      contact: {
                        id: 123,
                        name: 'João Silva',
                        email: 'joao@email.com',
                        phone_number: '+5511999999999',
                      },
                      resource: { id: 1, name: 'Dr. Ana' },
                      meet_url: 'https://meet.google.com/xyz-abc-def',
                      schedule: { id: 1, title: 'Consulta', slug: 'consulta' },
                    },
                    null,
                    2
                  )
                }}</pre>
            </div>
          </div>

          <!-- ── GOOGLE CALENDAR / MEET ── -->
          <div
            class="bg-n-background border border-n-weak rounded-2xl p-5 space-y-4"
          >
            <div class="flex items-center gap-2">
              <span class="i-lucide-video size-4 text-n-slate-9" />
              <h3 class="text-sm font-semibold text-n-slate-12">
                Google Agenda & Meet
              </h3>
            </div>

            <!-- Connected state -->
            <div v-if="googleConnectedEmail" class="space-y-4">
              <div
                class="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4"
              >
                <span
                  class="i-lucide-check-circle-2 size-5 text-emerald-600 flex-shrink-0"
                />
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-semibold text-emerald-900">
                    Conectado
                  </p>
                  <p class="text-xs text-emerald-700 truncate">
                    {{ googleConnectedEmail }}
                  </p>
                </div>
                <button
                  class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-red-600 border border-red-200 bg-white hover:bg-red-50 transition-all flex-shrink-0"
                  @click="doDisconnectGoogle"
                >
                  <span class="i-lucide-unlink size-3.5" />
                  Desconectar
                </button>
              </div>

              <!-- Meet toggle -->
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm font-medium text-n-slate-12">
                    Gerar link do Google Meet
                  </p>
                  <p class="text-xs text-n-slate-8 mt-0.5">
                    Cria automaticamente um evento e link Meet a cada
                    agendamento
                  </p>
                </div>
                <button
                  class="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all"
                  :class="
                    settingsForm.integration_settings.google_meet_enabled
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : 'bg-n-alpha-1 text-n-slate-9 border-n-weak hover:bg-n-alpha-2'
                  "
                  @click="
                    settingsForm.integration_settings.google_meet_enabled =
                      !settingsForm.integration_settings.google_meet_enabled
                  "
                >
                  <span
                    class="w-1.5 h-1.5 rounded-full"
                    :class="
                      settingsForm.integration_settings.google_meet_enabled
                        ? 'bg-emerald-500'
                        : 'bg-n-slate-5'
                    "
                  />
                  {{
                    settingsForm.integration_settings.google_meet_enabled
                      ? 'Ativo'
                      : 'Inativo'
                  }}
                </button>
              </div>

              <!-- Optional Calendar ID -->
              <div>
                <label class="block text-xs font-semibold text-n-slate-11 mb-1">
                  Calendar ID
                  <span class="font-normal text-n-slate-8 ml-1">(opcional — padrão: calendário principal)</span>
                </label>
                <input
                  v-model="settingsForm.integration_settings.calendar_id"
                  type="text"
                  placeholder="primary"
                  class="w-full px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 placeholder-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 font-mono transition-all"
                />
                <p class="text-xs text-n-slate-7 mt-1">
                  Deixe vazio para usar o calendário principal. Ou informe o
                  e-mail de um calendário compartilhado (ex:
                  <code class="bg-n-alpha-2 px-1 rounded">consultor.skyaigroup@gmail.com</code>).
                </p>
              </div>
              <!-- iCal URL (also works alongside OAuth) -->
              <div>
                <label class="block text-xs font-semibold text-n-slate-11 mb-1">
                  URL iCal secreto
                  <span class="font-normal text-n-slate-8 ml-1">(alternativa: captura Meet de eventos pré-criados)</span>
                </label>
                <input
                  v-model="settingsForm.integration_settings.ical_secret_url"
                  type="url"
                  placeholder="https://calendar.google.com/calendar/ical/.../private-.../basic.ics"
                  class="w-full px-3 py-2.5 text-xs rounded-xl border border-n-weak bg-n-background text-n-slate-12 placeholder-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 font-mono transition-all"
                />
              </div>
            </div>

            <!-- Disconnected state -->
            <div v-else class="space-y-4">
              <!-- Option A: iCal (simple, no OAuth) -->
              <div class="rounded-xl border border-n-weak overflow-hidden">
                <div
                  class="px-4 py-3 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2"
                >
                  <span class="i-lucide-link size-4 text-emerald-700" />
                  <p class="text-xs font-semibold text-emerald-900">
                    Opção A — URL iCal secreto (recomendado, sem OAuth)
                  </p>
                </div>
                <div class="p-4 space-y-3">
                  <p class="text-xs text-n-slate-9">
                    Crie seus eventos no Google Agenda
                    <strong>com Google Meet ativo</strong>. Cole abaixo o
                    <strong>endereço secreto no formato iCal</strong> da sua
                    agenda. Quando um agendamento for criado com o mesmo
                    horário, o link do Meet é capturado automaticamente.
                  </p>
                  <div
                    class="bg-n-alpha-1 rounded-lg p-2.5 text-xs text-n-slate-9 space-y-1"
                  >
                    <p class="font-semibold text-n-slate-11">
                      Como obter a URL iCal secreta:
                    </p>
                    <ol class="list-decimal list-inside space-y-0.5 ml-1">
                      <li>
                        Abra <strong>Google Agenda</strong> → configurações da
                        agenda
                      </li>
                      <li>Vá em <em>Integrar agenda</em></li>
                      <li>
                        Copie o
                        <strong>Endereço secreto no formato iCal</strong>
                      </li>
                    </ol>
                  </div>
                  <div>
                    <label
                      class="block text-xs font-semibold text-n-slate-11 mb-1"
                      >URL iCal secreto</label>
                    <div class="flex gap-2">
                      <input
                        v-model="
                          settingsForm.integration_settings.ical_secret_url
                        "
                        type="url"
                        placeholder="https://calendar.google.com/calendar/ical/.../private-.../basic.ics"
                        class="flex-1 px-3 py-2.5 text-xs rounded-xl border border-n-weak bg-n-background text-n-slate-12 placeholder-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 font-mono transition-all"
                      />
                      <button
                        class="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border border-n-weak bg-n-background hover:bg-n-alpha-2 text-n-slate-11 transition-all disabled:opacity-50"
                        :disabled="
                          isTestingIcal ||
                          !settingsForm.integration_settings.ical_secret_url
                        "
                        @click="testIcalConnection"
                      >
                        <span
                          v-if="isTestingIcal"
                          class="i-lucide-loader-2 size-3.5 animate-spin"
                        />
                        <span v-else class="i-lucide-plug-zap size-3.5" />
                        {{ isTestingIcal ? 'Testando...' : 'Testar' }}
                      </button>
                    </div>

                    <!-- Test result -->
                    <div
                      v-if="icalTestResult"
                      class="mt-2 rounded-lg p-3 text-xs space-y-1.5"
                      :class="
                        icalTestResult.ok
                          ? 'bg-emerald-50 border border-emerald-200'
                          : 'bg-red-50 border border-red-200'
                      "
                    >
                      <div v-if="icalTestResult.ok" class="space-y-1">
                        <p
                          class="font-semibold text-emerald-900 flex items-center gap-1.5"
                        >
                          <span class="i-lucide-check-circle-2 size-3.5" />
                          Conexão OK — {{ icalTestResult.total_events }} eventos
                          encontrados, {{ icalTestResult.events_with_meet }} com
                          link do Meet
                        </p>
                        <div
                          v-if="icalTestResult.upcoming_with_meet?.length"
                          class="space-y-0.5 mt-1"
                        >
                          <p class="text-emerald-800 font-medium">
                            Próximos eventos com Meet:
                          </p>
                          <div
                            v-for="ev in icalTestResult.upcoming_with_meet"
                            :key="ev.meet_url"
                            class="flex items-center gap-2 text-emerald-700"
                          >
                            <span class="i-lucide-video size-3" />
                            <span>{{ ev.time }} — {{ ev.summary }}</span>
                            <a
                              :href="ev.meet_url"
                              target="_blank"
                              class="underline ml-auto"
                              >abrir</a>
                          </div>
                        </div>
                        <p v-else class="text-emerald-700">
                          Nenhum evento futuro com Meet encontrado. Crie eventos
                          no Google Agenda com Google Meet ativo.
                        </p>
                      </div>
                      <p
                        v-else
                        class="font-medium text-red-800 flex items-center gap-1.5"
                      >
                        <span class="i-lucide-x-circle size-3.5" />
                        {{ icalTestResult.error }}
                      </p>
                    </div>
                  </div>
                  <div class="flex items-center justify-between">
                    <div>
                      <p class="text-sm font-medium text-n-slate-12">
                        Ativar captura de Meet
                      </p>
                      <p class="text-xs text-n-slate-8 mt-0.5">
                        Busca o link do Meet no evento correspondente ao horário
                      </p>
                    </div>
                    <button
                      class="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all"
                      :class="
                        settingsForm.integration_settings.google_meet_enabled
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : 'bg-n-alpha-1 text-n-slate-9 border-n-weak hover:bg-n-alpha-2'
                      "
                      @click="
                        settingsForm.integration_settings.google_meet_enabled =
                          !settingsForm.integration_settings.google_meet_enabled
                      "
                    >
                      <span
                        class="w-1.5 h-1.5 rounded-full"
                        :class="
                          settingsForm.integration_settings.google_meet_enabled
                            ? 'bg-emerald-500'
                            : 'bg-n-slate-5'
                        "
                      />
                      {{
                        settingsForm.integration_settings.google_meet_enabled
                          ? 'Ativo'
                          : 'Inativo'
                      }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- Option B: OAuth (future) -->
              <div
                class="rounded-xl border border-n-weak overflow-hidden opacity-60"
              >
                <div
                  class="px-4 py-3 bg-n-alpha-1 border-b border-n-weak flex items-center gap-2"
                >
                  <span class="i-lucide-key-round size-4 text-n-slate-8" />
                  <p class="text-xs font-semibold text-n-slate-9">
                    Opção B — Conectar com Google (OAuth)
                  </p>
                  <span
                    class="ml-auto text-xs bg-n-alpha-2 text-n-slate-8 px-2 py-0.5 rounded-full"
                    >Requer configuração OAuth no servidor</span>
                </div>
                <div class="p-4">
                  <button
                    class="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium bg-white border border-n-weak shadow-sm hover:shadow transition-all disabled:opacity-50"
                    :disabled="isConnectingGoogle"
                    @click="connectGoogleCalendar"
                  >
                    <span
                      v-if="isConnectingGoogle"
                      class="i-lucide-loader-2 size-4 animate-spin text-n-slate-9"
                    />
                    <svg
                      v-else
                      xmlns="http://www.w3.org/2000/svg"
                      class="size-4"
                      viewBox="0 0 48 48"
                    >
                      <path
                        fill="#EA4335"
                        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                      />
                      <path
                        fill="#4285F4"
                        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                      />
                      <path
                        fill="#34A853"
                        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                      />
                      <path fill="none" d="M0 0h48v48H0z" />
                    </svg>
                    <span class="text-n-slate-12">{{
                      isConnectingGoogle
                        ? 'Redirecionando...'
                        : 'Conectar com Google'
                    }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Save + Danger -->
          <div class="flex items-center justify-between pt-2 pb-6">
            <button
              class="flex items-center gap-2 px-5 py-2.5 bg-n-brand text-white text-sm font-medium rounded-xl hover:opacity-90 disabled:opacity-50 transition-all"
              :disabled="isSavingSchedule"
              @click="saveSettings"
            >
              <span
                v-if="isSavingSchedule"
                class="i-lucide-loader-2 size-4 animate-spin"
              />
              <span v-else class="i-lucide-save size-4" />
              {{ isSavingSchedule ? 'Salvando...' : 'Salvar configurações' }}
            </button>

            <button
              class="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-xl border border-red-200 transition-all"
              @click="deleteSchedule"
            >
              <span class="i-lucide-trash-2 size-4" />
              Excluir agenda
            </button>
          </div>
        </div>
      </div>
    </main>

    <!-- ═══════════════════════════════════════════════════
         MODALS
    ═══════════════════════════════════════════════════ -->
    <AgendaBookingModal
      v-if="showBookingModal"
      :schedule="selectedSchedule"
      :resources="scheduleResources"
      :availability="availability"
      :preselected-date="selectedDate"
      :preselected-slot="preselectedSlot"
      :editing-booking="editingBooking"
      :is-saving="isSavingBooking"
      :origin-options="scheduleOriginOptions"
      @save="saveBooking"
      @update="updateBooking"
      @close="
        showBookingModal = false;
        preselectedSlot = null;
        editingBooking = null;
      "
    />

    <AgendaBookingModal
      v-if="showBlockModal"
      :schedule="selectedSchedule"
      :resources="scheduleResources"
      :availability="availability"
      :preselected-date="selectedDate"
      :editing-booking="isEditingBlock ? editingBooking : null"
      :is-saving="isSavingBooking"
      :is-block="true"
      :origin-options="scheduleOriginOptions"
      @save="saveBlock"
      @update="updateBlock"
      @close="
        showBlockModal = false;
        editingBooking = null;
        isEditingBlock = false;
      "
    />

    <AgendaScheduleModal
      v-if="showScheduleModal"
      :schedule="editingSchedule"
      :is-saving="isSavingSchedule"
      @save="saveSchedule"
      @close="
        showScheduleModal = false;
        editingSchedule = null;
      "
    />

    <!-- ═══════════════════════════════════════════════════
         MESSAGE MODAL
    ═══════════════════════════════════════════════════ -->
    <Teleport to="body">
      <div
        v-if="showMessageModal && messageModalBooking"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        @click.self="showMessageModal = false"
      >
        <div
          class="absolute inset-0 bg-black/40 backdrop-blur-sm"
          @click="showMessageModal = false"
        />

        <div
          class="relative bg-n-background rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        >
          <!-- Header -->
          <div
            class="flex items-center justify-between p-5 border-b border-n-weak"
          >
            <div class="flex items-center gap-3">
              <div
                class="w-8 h-8 rounded-xl flex items-center justify-center"
                :style="{
                  backgroundColor:
                    STATUS_COLOR[messageModalBooking.status] + '22',
                }"
              >
                <span
                  class="i-lucide-message-circle size-4"
                  :style="{ color: STATUS_COLOR[messageModalBooking.status] }"
                />
              </div>
              <div>
                <h2 class="text-sm font-semibold text-n-slate-12">
                  Enviar mensagem
                </h2>
                <p class="text-xs text-n-slate-8">
                  Para: {{ messageModalBooking.attendee_name }} ·
                  {{
                    format(
                      parseISO(messageModalBooking.starts_at),
                      'dd/MM/yyyy HH:mm'
                    )
                  }}
                </p>
              </div>
            </div>
            <button
              class="p-2 rounded-lg hover:bg-n-alpha-2 text-n-slate-9 transition-colors"
              @click="showMessageModal = false"
            >
              <span class="i-lucide-x size-4" />
            </button>
          </div>

          <div class="p-5 space-y-4">
            <!-- Inbox selector -->
            <div>
              <label class="block text-xs font-semibold text-n-slate-11 mb-1.5">
                Canal / Caixa de entrada
              </label>
              <select
                v-model="selectedInboxId"
                class="w-full px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all"
              >
                <option v-if="inboxes.length === 0" :value="null">
                  Carregando canais...
                </option>
                <option
                  v-for="inbox in inboxes"
                  :key="inbox.id"
                  :value="inbox.id"
                >
                  {{ inbox.name }}
                </option>
              </select>
            </div>

            <!-- Message draft -->
            <div>
              <div class="flex items-center justify-between mb-1.5">
                <label class="text-xs font-semibold text-n-slate-11">Mensagem</label>
                <button
                  class="text-xs text-n-brand hover:underline"
                  @click="messageDraft = buildMessageDraft(messageModalBooking)"
                >
                  Restaurar padrão
                </button>
              </div>
              <textarea
                v-model="messageDraft"
                rows="8"
                class="w-full px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 placeholder-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all resize-none font-mono leading-relaxed"
              />
              <p class="text-xs text-n-slate-7 mt-1">
                A mensagem será enviada como nova conversa no canal selecionado.
              </p>
            </div>

            <!-- No contact warning -->
            <div
              v-if="!messageModalBooking.metadata?.contact_id"
              class="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl"
            >
              <span
                class="i-lucide-triangle-alert size-4 text-amber-600 flex-shrink-0 mt-0.5"
              />
              <p class="text-xs text-amber-800">
                Este agendamento não tem um contato Chatwoot vinculado. Vincule
                um contato ao editar o agendamento para habilitar o envio.
              </p>
            </div>
          </div>

          <!-- Footer -->
          <div
            class="flex items-center justify-between p-5 border-t border-n-weak"
          >
            <button
              class="px-4 py-2 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2 rounded-xl border border-n-weak transition-colors"
              @click="showMessageModal = false"
            >
              Cancelar
            </button>
            <button
              :disabled="
                !selectedInboxId ||
                !messageDraft.trim() ||
                !messageModalBooking.metadata?.contact_id ||
                isSendingMessage
              "
              class="flex items-center gap-2 px-5 py-2 text-white text-sm font-medium rounded-xl hover:opacity-90 disabled:opacity-40 transition-all"
              style="background-color: #059669"
              @click="sendMessage"
            >
              <span
                v-if="isSendingMessage"
                class="i-lucide-loader-2 size-4 animate-spin"
              />
              <span v-else class="i-lucide-send size-4" />
              {{ isSendingMessage ? 'Enviando...' : 'Enviar mensagem' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Slot Detail Modal -->
    <Teleport to="body">
      <div
        v-if="showSlotDetail && slotDetailItem"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        @click.self="showSlotDetail = false"
      >
        <div
          class="absolute inset-0 bg-black/40 backdrop-blur-sm"
          @click="showSlotDetail = false"
        />

        <div
          class="relative bg-n-background rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        >
          <!-- Header -->
          <div
            class="flex items-center justify-between p-5 border-b border-n-weak"
          >
            <div>
              <div class="flex items-center gap-2 mb-0.5">
                <span class="i-lucide-clock size-4 text-n-slate-9" />
                <h2 class="text-sm font-semibold text-n-slate-12">
                  {{ format(parseISO(slotDetailItem.slot.starts_at), 'HH:mm') }}
                  –
                  {{ format(parseISO(slotDetailItem.slot.ends_at), 'HH:mm') }}
                </h2>
              </div>
              <p class="text-xs text-n-slate-8 ml-6">
                {{
                  format(parseISO(slotDetailItem.slot.starts_at), 'dd/MM/yyyy')
                }}
                · {{ slotDetailItem.bookings.length }} agendamento{{
                  slotDetailItem.bookings.length !== 1 ? 's' : ''
                }}
                · {{ slotDetailItem.slot.remaining_slots }} vaga{{
                  slotDetailItem.slot.remaining_slots !== 1 ? 's' : ''
                }}
                disponível{{
                  slotDetailItem.slot.remaining_slots !== 1 ? 'is' : ''
                }}
              </p>
            </div>
            <button
              class="p-2 rounded-lg hover:bg-n-alpha-2 text-n-slate-9 transition-colors"
              @click="showSlotDetail = false"
            >
              <span class="i-lucide-x size-4" />
            </button>
          </div>

          <!-- Capacity bar -->
          <div class="px-5 pt-4">
            <div
              class="flex items-center justify-between text-xs text-n-slate-8 mb-1.5"
            >
              <span>Ocupação do slot</span>
              <span class="font-medium">
                {{ slotDetailItem.slot.reserved_slots || 0 }} /
                {{
                  (slotDetailItem.slot.reserved_slots || 0) +
                  (slotDetailItem.slot.remaining_slots || 0)
                }}
                vagas
              </span>
            </div>
            <div class="h-2 bg-n-alpha-2 rounded-full overflow-hidden">
              <div
                class="h-full rounded-full transition-all"
                :style="{
                  width: slotOccupationWidth(slotDetailItem.slot),
                  backgroundColor: slotFillColor(slotDetailItem.slot),
                }"
              />
            </div>
          </div>

          <!-- Bookings list -->
          <div class="p-5 space-y-2 max-h-72 overflow-y-auto">
            <p
              v-if="slotDetailItem.bookings.length === 0"
              class="text-xs text-n-slate-8 text-center py-4"
            >
              Nenhum agendamento neste slot.
            </p>

            <div
              v-for="booking in slotDetailItem.bookings"
              :key="booking.id"
              class="flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer hover:shadow-sm"
              :class="statusConfig(booking.status).classes"
              @click="
                showSlotDetail = false;
                openEditBooking(booking);
              "
            >
              <!-- Avatar -->
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                :style="{
                  backgroundColor: booking.resource?.color || '#6366f1',
                }"
              >
                {{ (booking.attendee_name || '?')[0].toUpperCase() }}
              </div>

              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <span class="text-xs font-semibold text-n-slate-12 truncate">
                    {{ booking.attendee_name || 'Sem nome' }}
                  </span>
                  <span
                    v-if="booking.slot_quantity > 1"
                    class="flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs font-semibold bg-black/10 rounded-full"
                  >
                    ×{{ booking.slot_quantity }} vagas
                  </span>
                </div>
                <div class="text-xs text-n-slate-8 truncate mt-0.5">
                  <span v-if="booking.resource" class="font-medium">{{
                    booking.resource.name
                  }}</span>
                  <span v-if="booking.resource && booking.attendee_email">
                    ·
                  </span>
                  <span>{{
                    booking.attendee_email || booking.attendee_phone || ''
                  }}</span>
                </div>
              </div>

              <div class="flex items-center gap-1.5 flex-shrink-0">
                <span class="i-lucide-pencil size-3.5 text-n-slate-7" />
              </div>
            </div>
          </div>

          <!-- Footer actions -->
          <div
            class="flex items-center justify-between p-5 border-t border-n-weak"
          >
            <button
              class="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2 rounded-lg border border-n-weak transition-colors"
              @click="showSlotDetail = false"
            >
              Fechar
            </button>
            <button
              class="flex items-center gap-1.5 px-4 py-2 bg-n-brand text-white text-xs font-medium rounded-lg hover:opacity-90 transition-all"
              @click="
                showSlotDetail = false;
                openNewBooking(slotDetailItem.slot);
              "
            >
              <span class="i-lucide-plus size-3.5" />
              Novo agendamento
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ═══════════════════════════════════════════════════
         QUICK CONTACT MODAL
    ═══════════════════════════════════════════════════ -->
    <Teleport to="body">
      <div
        v-if="showQuickContactModal"
        class="fixed inset-0 z-[200] flex items-center justify-center"
        @click.self="showQuickContactModal = false"
      >
        <div
          class="absolute inset-0 bg-black/40 backdrop-blur-sm"
          @click="showQuickContactModal = false"
        />

        <div
          class="relative z-10 bg-n-background rounded-2xl shadow-2xl border border-n-weak w-full max-w-sm mx-4 overflow-hidden"
        >
          <!-- Header -->
          <div
            class="flex items-center justify-between px-5 py-4 border-b border-n-weak"
          >
            <div class="flex items-center gap-2.5">
              <div
                class="w-8 h-8 rounded-lg bg-n-alpha-2 flex items-center justify-center"
              >
                <span class="i-lucide-user-plus size-4 text-n-brand" />
              </div>
              <div>
                <h3 class="text-sm font-semibold text-n-slate-12">
                  Novo contato
                </h3>
                <p class="text-xs text-n-slate-9">Cadastro rápido</p>
              </div>
            </div>
            <button
              class="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-n-alpha-2 text-n-slate-8 hover:text-n-slate-12 transition-colors"
              @click="showQuickContactModal = false"
            >
              <span class="i-lucide-x size-4" />
            </button>
          </div>

          <!-- Body -->
          <div class="px-5 py-4 space-y-4">
            <!-- Name -->
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1.5">
                Nome <span class="text-red-500">*</span>
              </label>
              <input
                v-model="quickContactName"
                type="text"
                placeholder="Nome completo"
                class="w-full px-3 py-2 text-sm bg-n-alpha-1 border border-n-weak rounded-xl text-n-slate-12 placeholder-n-slate-7 focus:outline-none focus:border-n-brand focus:ring-1 focus:ring-n-brand/30 transition-colors"
                @keydown.enter="saveQuickContact"
              />
            </div>

            <!-- Phone -->
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1.5">
                Telefone / WhatsApp <span class="text-red-500">*</span>
              </label>
              <div class="flex items-center gap-2">
                <span
                  class="flex-shrink-0 px-3 py-2 text-sm bg-n-alpha-2 border border-n-weak rounded-xl text-n-slate-11 font-medium select-none"
                >
                  🇧🇷 +55
                </span>
                <input
                  :value="quickContactPhoneDisplay"
                  type="tel"
                  inputmode="numeric"
                  placeholder="(11) 99999-9999"
                  maxlength="15"
                  class="flex-1 px-3 py-2 text-sm bg-n-alpha-1 border border-n-weak rounded-xl text-n-slate-12 placeholder-n-slate-7 focus:outline-none focus:border-n-brand focus:ring-1 focus:ring-n-brand/30 transition-colors"
                  @input="onQuickPhoneInput"
                  @keydown.enter="saveQuickContact"
                />
              </div>
              <p class="mt-1.5 text-xs text-n-slate-8">
                Formato E.164 — salvo como
                <span class="font-mono text-n-slate-10">
                  {{ quickContactE164 || '+55...' }}
                </span>
              </p>
            </div>
          </div>

          <!-- Footer -->
          <div
            class="flex items-center justify-end gap-2 px-5 py-4 border-t border-n-weak bg-n-alpha-1/50"
          >
            <button
              class="px-4 py-2 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2 rounded-lg transition-colors"
              @click="showQuickContactModal = false"
            >
              Cancelar
            </button>
            <button
              class="flex items-center gap-1.5 px-4 py-2 text-xs font-medium bg-n-brand text-white rounded-lg hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="
                isSavingQuickContact ||
                !quickContactName.trim() ||
                !quickContactE164
              "
              @click="saveQuickContact"
            >
              <span
                v-if="isSavingQuickContact"
                class="i-lucide-loader-2 size-3.5 animate-spin"
              />
              <span v-else class="i-lucide-user-plus size-3.5" />
              Salvar contato
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
